import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useWebSocket } from "./WebSocketContext";
import {
  Box,
  Button,
  Stack,
  TextField,
  FormControlLabel,
  Typography,
  Tooltip,
  IconButton,
  Paper,
  Badge,
  Chip,
  CircularProgress,
  Fade,
  Grow,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  Slider,
  InputAdornment,
  useTheme,
  useMediaQuery,
  ButtonBase,
  Divider
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import ErrorIcon from '@mui/icons-material/Error';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HistoryIcon from '@mui/icons-material/History';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Popover from '@mui/material/Popover';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseButton from './Common/CloseButton';
import GenericConnectorIngestions from './GenericConnectorIngestions';
import GenericConnectorWizard from './GenericConnectorWizard';
import { useFolderPicker } from './useFolderPicker';
import { useFolderRestrictions } from './useFolderRestrictions';
import PluginMemoryDialog from './PluginMemoryDialog';
import icon_default from '../images/logotransparent.png';

// Define theme colors for consistent styling
const themeColors = {
  primary: '#5757b0',
  primaryLight: '#7878c5',
  primaryDark: '#3a3a8c',
  accent: '#ff8a65',
  text: '#333',
  lightText: '#f5f5f5',
  background: '#f8f9fa',
  cardBackground: '#ffffff'
};
const toggleColors = {
  on: '#4caf50',
  off: '#bdbdbd'
};
const toggleIconSizes = {
  mobile: 32,
  desktop: 40,
  list: 36
};
const NEW_DATASOURCE_DEFAULT_INTERVAL = 1800;

const GENERIC_PLUGIN_CONNECTOR_OAUTH_COMMANDS = {
  authorize: 'authorizePluginConnectorOAuth',
  authorizeStart: 'authorizePluginConnectorOAuthStart',
  authorizeError: 'authorizePluginConnectorOAuthError',
  verify: 'verifyPluginConnectorOAuthToken',
  verifyResult: 'verifyPluginConnectorOAuthTokenResult',
  tokenResult: 'pluginConnectorOAuthTokenResult',
};

const coerceBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'y', 'on'].includes(normalized)) {
      return true;
    }
    if (['0', 'false', 'no', 'n', 'off'].includes(normalized)) {
      return false;
    }
  }
  return fallback;
};

const normalizeConnectorPluginOAuthConfig = (rawConfig) => {
  if (!rawConfig || typeof rawConfig !== 'object') {
    return null;
  }

  const providerKey = String(rawConfig.provider || '').trim().toLowerCase();
  if (!providerKey) {
    return null;
  }

  const authKey = String(rawConfig.authKey || `${providerKey}Auth`).trim();
  const label = String(rawConfig.label || providerKey).trim() || providerKey;
  const runtimeMode = String(rawConfig.runtimeMode || rawConfig.runtime_mode || '').trim().toLowerCase();
  const normalizeOptionalText = (...keys) => {
    for (const key of keys) {
      const value = rawConfig[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  };
  const missingFields = Array.isArray(rawConfig.missingFields)
    ? rawConfig.missingFields
      .map((field) => String(field || '').trim())
      .filter(Boolean)
    : [];

  return {
    provider: providerKey,
    label,
    authKey: authKey || `${providerKey}Auth`,
    accountRequired: coerceBoolean(rawConfig.accountRequired, true),
    helperText: typeof rawConfig.helperText === 'string' ? rawConfig.helperText.trim() : '',
    connectLabel: typeof rawConfig.connectLabel === 'string' ? rawConfig.connectLabel.trim() : '',
    disconnectLabel: typeof rawConfig.disconnectLabel === 'string' ? rawConfig.disconnectLabel.trim() : '',
    runtimeMode,
    missingFields,
    authorizeUrl: normalizeOptionalText('authorizeUrl', 'authorize_url'),
    tokenUrl: normalizeOptionalText('tokenUrl', 'token_url'),
    userInfoUrl: normalizeOptionalText('userInfoUrl', 'user_info_url'),
    redirectUri: normalizeOptionalText('redirectUri', 'redirect_uri'),
    clientId: normalizeOptionalText('clientId', 'client_id'),
    clientIdEnv: normalizeOptionalText('clientIdEnv', 'client_id_env'),
    clientSecret: normalizeOptionalText('clientSecret', 'client_secret'),
    clientSecretEnv: normalizeOptionalText('clientSecretEnv', 'client_secret_env'),
    commands: GENERIC_PLUGIN_CONNECTOR_OAUTH_COMMANDS,
  };
};

const formatUtcTimestamp = (date) => {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
};

// Reusable styled components
const StyledTextField = ({ disabled, sx, ...props }) => (
  <TextField
    disabled={disabled}
    variant="outlined"
    fullWidth
    sx={[
      {
        '& .MuiOutlinedInput-root': {
          '&.Mui-focused fieldset': {
            borderColor: themeColors.primary,
          },
        },
        '& label.Mui-focused': {
          color: themeColors.primary,
        },
        transition: 'all 0.3s ease',
        mb: 3
      },
      sx
    ]}
    {...props}
  />
);

const FileBrowserInput = ({ component, value, onChange, disabled, folderContext }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value ?? '');
  const folderRestrictions = folderContext?.folderRestrictions || {};

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);
    onChange(component.name, nextValue);
  };

  const handleFolderSelect = useCallback((selectedPath) => {
    const nextValue = selectedPath || '';
    setInputValue(nextValue);
    onChange(component.name, nextValue);
  }, [component.name, onChange]);

  const {
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    loading
  } = folderRestrictions;

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    ws: folderContext?.ws,
    onFolderSelected: handleFolderSelect,
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true
  });

  return (
    <>
      <StyledTextField
        label={t(component.label) || component.label}
        name={component.name}
        value={inputValue}
        onChange={handleInputChange}
        placeholder={component.placeholder || t('generic_connector.file_browser_placeholder', 'Select file path')}
        disabled={disabled}
        helperText={component.helperText || t('generic_connector.file_browser_helper', 'Choose a file from storage')}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={t('generic_connector.browse_file', 'Browse file')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleBrowseDirectory}
                    disabled={disabled || loading}
                  >
                    <FolderOpenIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          )
        }}
      />
      {FolderBrowserComponent}
    </>
  );
};

const StyledPaper = ({ children, ...props }) => (
  <Paper
    elevation={0}
    sx={{
      p: props.condensed ? 2 : 3,
      mb: 3,
      borderRadius: 2,
      background: props.condensed ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)',
      ...(props.withBorder && { border: '1px solid rgba(200, 200, 200, 0.3)' }),
      backdropFilter: props.withBlur ? 'blur(10px)' : 'none',
      ...props.sx
    }}
  >
    {children}
  </Paper>
);

const ActionButton = ({ variant, icon, label, onClick, disabled, loading, success, sx }) => (
  <Button
    variant={variant}
    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : icon}
    onClick={onClick}
    disabled={disabled || loading}
    sx={{
      ...(variant === 'contained' ? {
        backgroundColor: themeColors.primary,
        color: themeColors.lightText,
        '&:hover': { backgroundColor: themeColors.primaryLight },
        '&.Mui-disabled': {
          backgroundColor: 'rgba(87, 87, 176, 0.4)',
          color: 'rgba(255, 255, 255, 0.6)'
        },
        boxShadow: '0 4px 10px rgba(87, 87, 176, 0.3)',
        '&:not(.Mui-disabled):hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 12px rgba(87, 87, 176, 0.4)',
        },
      } : {
        color: themeColors.primary,
        borderColor: themeColors.primary,
        '&:hover': {
          borderColor: themeColors.primaryLight,
          backgroundColor: 'rgba(87, 87, 176, 0.04)'
        },
      }),
      borderRadius: 2,
      px: 3,
      transition: 'all 0.3s ease',
      ...sx
    }}
  >
    {success ? success : label}
  </Button>
);

// Component to render dynamic form controls based on connector UI definition
const DynamicFormControl = ({ component, value, onChange, disabled, folderPickerContext }) => {
  const { t } = useTranslation();

  // If component is undefined or not properly formatted, return nothing
  if (!component || !component.type) {
    console.error("Invalid component configuration:", component);
    return null;
  }

  const componentType = (component.type || '').toLowerCase();
  switch (componentType) {
    case 'text':
      return (
        <StyledTextField
          label={t(component.label) || component.label}
          name={component.name}
          value={value ?? component.value ?? ''}
          onChange={(e) => onChange(component.name, e.target.value)}
          placeholder={component.placeholder || ''}
          helperText={component.helperText ? (t(component.helperText) || component.helperText) : undefined}
          disabled={disabled}
          type="text"
        />
      );
    case 'filebrowser':
      return (
        <FileBrowserInput
          component={component}
          value={value}
          onChange={onChange}
          disabled={disabled}
          folderContext={folderPickerContext}
        />
      );
    case 'password':
      return (
        <StyledTextField
          label={t(component.label) || component.label}
          name={component.name}
          value={value ?? component.value ?? ''}
          onChange={(e) => onChange(component.name, e.target.value)}
          placeholder={component.placeholder || ''}
          helperText={component.helperText ? (t(component.helperText) || component.helperText) : undefined}
          disabled={disabled}
          type="password"
        />
      );
    case 'number':
      return (
        <StyledTextField
          label={t(component.label) || component.label}
          name={component.name}
          value={value ?? component.value ?? ''}
          onChange={(e) => onChange(component.name, e.target.value)}
          placeholder={component.placeholder || ''}
          helperText={component.helperText ? (t(component.helperText) || component.helperText) : undefined}
          disabled={disabled}
          type="number"
          InputProps={{ inputProps: { min: component.min, max: component.max } }}
        />
      );

    case 'select':
      return (
        <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
          <InputLabel>{t(component.label) || component.label}</InputLabel>
          <Select
            value={value ?? component.value ?? ''}
            onChange={(e) => onChange(component.name, e.target.value)}
            label={t(component.label) || component.label}
            disabled={disabled}
          >
            {component.options && component.options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label) || option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );

    case 'checkbox':
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!value}
              onChange={(e) => onChange(component.name, e.target.checked)}
              disabled={disabled}
            />
          }
          label={t(component.label) || component.label}
          sx={{ mb: 2 }}
        />
      );

    case 'textarea':
      return (
        <StyledTextField
          label={t(component.label) || component.label}
          name={component.name}
          value={value ?? component.value ?? ''}
          onChange={(e) => onChange(component.name, e.target.value)}
          placeholder={component.placeholder || ''}
          helperText={component.helperText ? (t(component.helperText) || component.helperText) : undefined}
          disabled={disabled}
          multiline
          rows={component.rows || 4}
          onKeyDown={(e) => {
            if (e.key === 'Tab') {
              e.preventDefault();
              const start = e.target.selectionStart;
              const end = e.target.selectionEnd;
              const val = e.target.value;
              const newValue = val.substring(0, start) + '    ' + val.substring(end);

              // Call the onChange handler manually since we're manipulating the value
              onChange(component.name, newValue);

              // Need to defer setting selection range until after render
              setTimeout(() => {
                e.target.selectionStart = e.target.selectionEnd = start + 4;
              }, 0);
            }
          }}
          sx={{
            fontFamily: 'monospace',
            '& .MuiInputBase-input': {
              fontFamily: 'monospace', // Ensure code-like font
            }
          }}
        />
      );

    case 'slider':
      return (
        <Box sx={{ mb: 3 }}>
          <Typography gutterBottom>
            {t(component.label) || component.label}
          </Typography>
          <Slider
            value={value ?? component.value ?? 0}
            onChange={(_, newValue) => onChange(component.name, newValue)}
            aria-labelledby={`slider-${component.name}`}
            valueLabelDisplay="auto"
            step={component.step || 1}
            min={component.min || 0}
            max={component.max || 100}
            disabled={disabled}
          />
        </Box>
      );

    default:
      return (
        <Typography color="error">
          Unknown component type: {component.type}
        </Typography>
      );
  }
};

// Default MUI icon mappings for fallback
const ConnectorIcons = {
  news: <NewspaperIcon />,
  // Add more icons for other connector types
};

const CONNECTOR_PLUGIN_FIELD_TYPES = [
  "text",
  "textarea",
  "number",
  "password",
  "select",
  "checkbox",
  "filebrowser",
  "slider"
];

const CONNECTOR_PLUGIN_CLASS_TEMPLATE = `from .ConnectorParent import ConnectorParent

class ConnectorMySource(ConnectorParent):
    def __init__(self, exportFolder, last_update):
        super().__init__(exportFolder, last_update)
        self.title = "My Source Connector"
        self.components = [
            {
                "type": "text",
                "label": "Endpoint URL",
                "name": "endpoint",
                "value": "",
                "placeholder": "https://api.example.com/items"
            },
            {
                "type": "checkbox",
                "label": "Enable incremental sync",
                "name": "incremental",
                "value": True
            }
        ]`;

const CONNECTOR_PLUGIN_INGEST_TEMPLATE = `    def verify_connection(self) -> bool:
        # Validate credentials or endpoint quickly.
        return True

    def Ingest(self, datasource, case, force=False, requesting_user_id=None):
        # Pull source data and create/update events.
        return {
            "last_update": self.last_update,
            "should_queue": True,
            "queue_temp": False
        }`;

const getDefaultConnectorImage = (connectorType) => {
  const normalizedType = String(connectorType || '').trim();
  if (!normalizedType) {
    return icon_default;
  }
  return `/images/connectors/${normalizedType}.png`;
};

const getLowercaseConnectorImage = (connectorType) => {
  const normalizedType = String(connectorType || '').trim().toLowerCase();
  if (!normalizedType) {
    return icon_default;
  }
  return `/images/connectors/${normalizedType}.png`;
};

const GenericConnector = ({
  caseid,
  type,
  title,
  icon,
  useImageIcon = false,
  skipInitialDatasourceLoad = false,
  hasDatasourceProfileHint = false,
  hasDatasourceEnabledHint = false,
  guideUrl = "",
  forceOpen = false,
  onOpen = null,
  onDatasourceConfigured = null,
  beforeOpen = null,
  ensureCase = null,
  projectCreationWizardMode = false,
  aiGenerated = false,
  pluginOAuth = null,
  pluginMemory = null,
  loadFailed = false,
  loadError = "",
}) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages, serverInfo: wsServerInfo } = useWebSocket();
  const folderRestrictions = useFolderRestrictions(caseid);
  const folderPickerContext = useMemo(() => ({
    ws,
    folderRestrictions
  }), [ws, folderRestrictions]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Calculate responsive sizes
  const iconSize = isMobile ? 32 : isTablet ? 38 : 42;
  const containerPadding = isMobile ? '6px' : '8px';
  const progressSize = isMobile ? 44 : isTablet ? 52 : 58;
  const progressOffset = isMobile ? '-6px' : '-8px';
  const captionFontSize = isMobile ? '0.7rem' : '0.75rem';

  // State management
  const [connectorEnabled, setConnectorEnabled] = useState(false);
  const [connectorUI, setConnectorUI] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [open, setOpen] = useState(false);
  const [isCreationWizardActive, setIsCreationWizardActive] = useState(false);
  const [isStandaloneCreationWizard, setIsStandaloneCreationWizard] = useState(false);
  const [creationWizardStep, setCreationWizardStep] = useState(0);
  const [creationWizardIngestionMode, setCreationWizardIngestionMode] = useState('later');
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showPluginGuide, setShowPluginGuide] = useState(false);
  const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);
  const [isPluginMemoryDialogOpen, setIsPluginMemoryDialogOpen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(0);
  const [lastExecution, setLastExecution] = useState(0);
  const [lastUpdateInput, setLastUpdateInput] = useState(null);
  const [lastFoundCount, setLastFoundCount] = useState(0);
  const [lastSkippedCount, setLastSkippedCount] = useState(0);
  const [interval, setInterval] = useState(NEW_DATASOURCE_DEFAULT_INTERVAL);
  const [iconError, setIconError] = useState(false);
  const [imageSrc, setImageSrc] = useState(() => getDefaultConnectorImage(type));
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [anchorInfoEl, setAnchorInfoEl] = useState(null);
  const [anchorInfoPos, setAnchorInfoPos] = useState(null);
  const [anchorInfoContent, setAnchorInfoContent] = useState(null);
  const handleInfoClose = () => {
    setAnchorInfoEl(null);
    setAnchorInfoPos(null);
    setAnchorInfoContent(null);
  };

  // Centralized helper to open/close the shared info popover
  const handleOpenInfo = (e, text) => {
    e.stopPropagation();
    const tooltipText = text || '';
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const pos = { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX + (rect.width / 2) };
    // toggle if same element
    if (anchorInfoEl && anchorInfoEl === el) {
      handleInfoClose();
    } else {
      setAnchorInfoEl(el);
      setAnchorInfoPos(pos);
      setAnchorInfoContent(tooltipText);
    }
  };

  // Reusable small info button (consistent across the component)
  const InfoButton = ({ textKey, text, ariaLabel, sx }) => (
    <Tooltip title={t(textKey) || text} arrow>
      <IconButton
        size="small"
        sx={{ color: themeColors.primary, p: 0.5, width: 32, height: 32, ...sx }}
        aria-label={ariaLabel || 'info'}
        onClick={(e) => handleOpenInfo(e, t(textKey) || text)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpenInfo(e, t(textKey) || text);
          }
        }}
      >
        <InfoIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
  const [datasourceList, setDatasourceList] = useState([]);
  const [selectedDatasourceId, setSelectedDatasourceId] = useState(null);
  const [scanningDatasourceIds, setScanningDatasourceIds] = useState(new Set());
  const [datasourceName, setDatasourceName] = useState('');
  const [hasAnyEnabled, setHasAnyEnabled] = useState(false);
  const [hasAnyConnected, setHasAnyConnected] = useState(false);
  const [pluginOAuthAccount, setPluginOAuthAccount] = useState('');
  const [pluginOAuthOverrides, setPluginOAuthOverrides] = useState({
    authorizeUrl: '',
    tokenUrl: '',
    clientId: '',
    clientIdEnv: '',
    clientSecret: '',
    clientSecretEnv: '',
  });
  const [pluginOAuthStatus, setPluginOAuthStatus] = useState('disconnected');
  const [pluginOAuthMessage, setPluginOAuthMessage] = useState('');
  const [pluginOAuthVerified, setPluginOAuthVerified] = useState(false);
  const [pluginOAuthAuthorizing, setPluginOAuthAuthorizing] = useState(false);
  const [pluginOAuthChecking, setPluginOAuthChecking] = useState(false);
  const [runtimePluginOAuth, setRuntimePluginOAuth] = useState(null);
  const [runtimePluginMemory, setRuntimePluginMemory] = useState(null);
  // Inline name editing states
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [savingNameId, setSavingNameId] = useState(null);
  const [savedNameSuccessId, setSavedNameSuccessId] = useState(null);
  const [forceRunLoading, setForceRunLoading] = useState(false);
  const [forceRunStatus, setForceRunStatus] = useState('');
  const [forceRunMessage, setForceRunMessage] = useState('');
  const [lastUpdateSaving, setLastUpdateSaving] = useState(false);
  const [lastUpdateSaved, setLastUpdateSaved] = useState(false);
  const [isEditingLastUpdate, setIsEditingLastUpdate] = useState(false);
  const [togglingDatasourceIds, setTogglingDatasourceIds] = useState(new Set());
  const [ingestionsOpen, setIngestionsOpen] = useState(false);
  const [ingestionsLoading, setIngestionsLoading] = useState(false);
  const [ingestionsError, setIngestionsError] = useState("");
  const [ingestionItems, setIngestionItems] = useState([]);
  const [ingestionFilter, setIngestionFilter] = useState("all");
  const normalizedConnectorType = useMemo(() => String(type || '').toLowerCase(), [type]);
  const effectivePluginOAuth = useMemo(() => {
    if (pluginOAuth && typeof pluginOAuth === 'object') {
      return pluginOAuth;
    }
    if (runtimePluginOAuth && typeof runtimePluginOAuth === 'object') {
      return runtimePluginOAuth;
    }
    return null;
  }, [pluginOAuth, runtimePluginOAuth]);
  const effectivePluginMemory = useMemo(() => {
    if (pluginMemory && typeof pluginMemory === 'object') {
      return pluginMemory;
    }
    if (runtimePluginMemory && typeof runtimePluginMemory === 'object') {
      return runtimePluginMemory;
    }
    return null;
  }, [pluginMemory, runtimePluginMemory]);
  const hasLoadFailure = Boolean(loadFailed);
  const loadFailureMessage = useMemo(() => {
    const trimmed = String(loadError || '').trim();
    return trimmed || t('generic_connector.plugin_load_failed', 'Connector dependencies could not be installed.');
  }, [loadError, t]);
  const isPluginUnavailable = hasError || hasLoadFailure;
  const pluginOAuthConfig = useMemo(
    () => normalizeConnectorPluginOAuthConfig(effectivePluginOAuth),
    [effectivePluginOAuth]
  );
  const pluginOAuthAccountValue = useMemo(
    () => String(pluginOAuthAccount || '').trim(),
    [pluginOAuthAccount]
  );
  const pluginOAuthRuntimeIsGeneric = Boolean(
    pluginOAuthConfig && (
      !pluginOAuthConfig.runtimeMode
      || pluginOAuthConfig.runtimeMode === 'generic'
    )
  );
  const buildPluginOAuthConfigPayload = useCallback(() => {
    const source = pluginOAuthOverrides || {};
    const payload = {};
    const fields = [
      'authorizeUrl',
      'tokenUrl',
      'clientId',
      'clientIdEnv',
      'clientSecret',
      'clientSecretEnv',
    ];
    fields.forEach((field) => {
      const value = source[field];
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) {
          payload[field] = trimmed;
        }
      }
    });
    return payload;
  }, [pluginOAuthOverrides]);
  const pluginOAuthMissingRequiredFields = useMemo(() => {
    if (!pluginOAuthRuntimeIsGeneric || !pluginOAuthConfig) {
      return [];
    }
    const payload = buildPluginOAuthConfigPayload();
    const effectiveAuthorizeUrl = String(payload.authorizeUrl || pluginOAuthConfig.authorizeUrl || '').trim();
    const effectiveTokenUrl = String(payload.tokenUrl || pluginOAuthConfig.tokenUrl || '').trim();
    const effectiveClientId = String(payload.clientId || pluginOAuthConfig.clientId || '').trim();
    const effectiveClientIdEnv = String(payload.clientIdEnv || pluginOAuthConfig.clientIdEnv || '').trim();
    const missing = [];
    if (!effectiveAuthorizeUrl) {
      missing.push('authorizeUrl');
    }
    if (!effectiveTokenUrl) {
      missing.push('tokenUrl');
    }
    if (!effectiveClientId && !effectiveClientIdEnv) {
      missing.push('clientId/clientIdEnv');
    }
    return missing;
  }, [pluginOAuthRuntimeIsGeneric, pluginOAuthConfig, buildPluginOAuthConfigPayload]);
  const resolveGuideUrl = useCallback((rawGuideUrl) => {
    const raw = typeof rawGuideUrl === "string" ? rawGuideUrl.trim() : "";
    if (!raw) {
      return "";
    }
    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }
    if (!raw.startsWith("/")) {
      return raw;
    }
    const protocol = String(wsServerInfo?.webServerScheme || window.location.protocol.replace(":", "") || "http");
    const hostname = String(window.location.hostname || "localhost");
    const backendPort = Number(wsServerInfo?.webServerPort);
    if (Number.isInteger(backendPort) && backendPort > 0) {
      return `${protocol}://${hostname}:${backendPort}${raw}`;
    }
    return `${window.location.origin}${raw}`;
  }, [wsServerInfo]);
  const normalizedGuideUrl = useMemo(
    () => resolveGuideUrl(guideUrl),
    [guideUrl, resolveGuideUrl]
  );
  const hasConnectorGuide = normalizedGuideUrl.length > 0;
  const connectorGuideButtonLabel = t('generic_connector.guide_button', { defaultValue: "Guide" });
  const connectorGuideTooltipLabel = t('generic_connector.open_guide', { defaultValue: "Open setup guide" });
  const connectorGuideDialogTitle = String(title || type || connectorGuideButtonLabel).trim();
  const supportsSinceDate = useMemo(
    () => normalizedConnectorType === 'outlookapi' || normalizedConnectorType === 'gmail',
    [normalizedConnectorType]
  );
  const defaultSinceDate = useMemo(() => dayjs().subtract(6, 'month'), []);

  useEffect(() => {
    if (!pluginOAuthConfig) {
      setPluginOAuthAccount('');
      setPluginOAuthOverrides({
        authorizeUrl: '',
        tokenUrl: '',
        clientId: '',
        clientIdEnv: '',
        clientSecret: '',
        clientSecretEnv: '',
      });
      setPluginOAuthStatus('disconnected');
      setPluginOAuthMessage('');
      setPluginOAuthVerified(false);
      setPluginOAuthAuthorizing(false);
      setPluginOAuthChecking(false);
    }
  }, [pluginOAuthConfig]);
  useEffect(() => {
    setRuntimePluginOAuth(null);
    setRuntimePluginMemory(null);
    lastLoadedDatasourceKeyRef.current = null;
  }, [caseid, type]);

  // To store the initial settings once they're loaded
  const initialSettings = useRef(null);
  const lastLoadedDatasourceKeyRef = useRef(null);
  const dismissedCreationWizardRef = useRef(false);
  const pendingWizardActionRef = useRef(null);
  const authWindowRef = useRef(null);
  const pluginOAuthAuthorizeTimeoutRef = useRef(null);
  const forceRunTimeoutRef = useRef(null);
  const lastUpdateBackup = useRef(null);
  useEffect(() => {
    setIconError(false);
    setImageSrc(getDefaultConnectorImage(type));
  }, [type]);

  const clearPluginOAuthAuthorizeTimeout = useCallback(() => {
    if (pluginOAuthAuthorizeTimeoutRef.current) {
      window.clearTimeout(pluginOAuthAuthorizeTimeoutRef.current);
      pluginOAuthAuthorizeTimeoutRef.current = null;
    }
  }, []);

  const startPluginOAuthAuthorizeTimeout = useCallback(() => {
    clearPluginOAuthAuthorizeTimeout();
    pluginOAuthAuthorizeTimeoutRef.current = window.setTimeout(() => {
      setPluginOAuthAuthorizing(false);
      setPluginOAuthStatus('disconnected');
      const timeoutMessage = t('generic_connector.plugin_oauth.timeout') || 'Unable to start OAuth. Check settings and try again.';
      setPluginOAuthMessage(timeoutMessage);
      setHasError(true);
      setErrorMessage(timeoutMessage);
    }, 15000);
  }, [clearPluginOAuthAuthorizeTimeout, t]);

  const clearForceRunTimeout = useCallback(() => {
    if (forceRunTimeoutRef.current) {
      window.clearTimeout(forceRunTimeoutRef.current);
      forceRunTimeoutRef.current = null;
    }
  }, []);

  const startForceRunTimeout = useCallback(() => {
    clearForceRunTimeout();
    forceRunTimeoutRef.current = window.setTimeout(() => {
      setForceRunLoading(false);
      setForceRunStatus('error');
      const timeoutMessage = t('generic_connector.force_run_timeout') || 'No completion signal received yet. Check ingestion history.';
      setForceRunMessage(timeoutMessage);
      setHasError(true);
      setErrorMessage(timeoutMessage);
    }, 45000);
  }, [clearForceRunTimeout, t]);

  useEffect(() => () => {
    clearPluginOAuthAuthorizeTimeout();
    clearForceRunTimeout();
  }, [clearPluginOAuthAuthorizeTimeout, clearForceRunTimeout]);

  const normalizeDatasourceId = (value) => {
    if (value === undefined || value === null) {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };
  const getDatasourceCacheKey = useCallback((datasourceId) => {
    if (!datasourceId) {
      return null;
    }
    return datasourceId === "new" ? "new" : normalizeDatasourceId(datasourceId);
  }, []);
  const ingestionLimit = 20;
  const requestDatasourceList = useCallback(() => {
    if (ws && caseid) {
      ws.send(JSON.stringify({
        command: "listGenericDatasources",
        caseid,
        type
      }));
    }
  }, [ws, caseid, type]);
  const requestDatasourceIngestions = useCallback(() => {
    if (!ws || !selectedDatasourceId || selectedDatasourceId === "new") {
      return;
    }
    setIngestionsLoading(true);
    setIngestionsError("");
    ws.send(JSON.stringify({
      command: "getDatasourceIngestions",
      caseid,
      type,
      datasource_id: selectedDatasourceId,
      limit: ingestionLimit
    }));
  }, [ws, caseid, type, selectedDatasourceId, ingestionLimit]);

  // Helper function to format seconds into readable time
  const formatTimeInterval = (seconds) => {
    if (seconds < 3600) {
      return `${Math.floor(seconds / 60)} minutes`;
    } else if (seconds < 86400) {
      return `${(seconds / 3600).toFixed(1)} hours`;
    } else {
      return `${(seconds / 86400).toFixed(1)} days`;
    }
  };

  const parseTimestampToDate = useCallback((value) => {
    if (value === undefined || value === null || value === "" || value === 0 || value === "0") {
      return null;
    }
    if (dayjs.isDayjs(value)) {
      return value.isValid() ? value.toDate() : null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === "number") {
      const parsedNumber = new Date(value);
      return Number.isNaN(parsedNumber.getTime()) ? null : parsedNumber;
    }
    if (typeof value !== "string") {
      return null;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    let normalized = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
    normalized = normalized.replace(/(\.\d{3})\d+/, "$1");
    const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized);
    // For timestamps without timezone info (e.g. "YYYY-MM-DD HH:mm:ss"),
    // interpret as local browser time instead of forcing UTC.
    const parsed = new Date(normalized);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
    if (!hasTimezone) {
      const parsedUtc = new Date(`${normalized}Z`);
      if (!Number.isNaN(parsedUtc.getTime())) {
        return parsedUtc;
      }
    }
    const fallback = new Date(trimmed);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }, []);

  const formatRelativeTimestamp = useCallback((timestamp) => {
    const parsedDate = parseTimestampToDate(timestamp);
    if (!parsedDate) {
      if (timestamp) {
        return String(timestamp);
      }
      return t('generic_connector.never_run', 'Never run');
    }
    const parsed = dayjs(parsedDate);
    const now = dayjs();
    const diffMinutes = now.diff(parsed, 'minute');
    if (diffMinutes < 1) {
      return t('generic_connector.just_now', 'Just now');
    }
    if (diffMinutes < 60) {
      return t('generic_connector.minutes_ago', { count: diffMinutes, defaultValue: `${diffMinutes} minutes ago` });
    }
    const diffHours = now.diff(parsed, 'hour');
    if (diffHours < 24) {
      return t('generic_connector.hours_ago', { count: diffHours, defaultValue: `${diffHours} hours ago` });
    }
    const diffDays = now.diff(parsed, 'day');
    return t('generic_connector.days_ago', { count: diffDays, defaultValue: `${diffDays} days ago` });
  }, [t, parseTimestampToDate]);

  const parseLastUpdateValue = useCallback((value) => {
    const parsedDate = parseTimestampToDate(value);
    return parsedDate ? dayjs(parsedDate) : null;
  }, [parseTimestampToDate]);

  const formatLastUpdateValue = useCallback((value) => {
    const parsedDate = parseTimestampToDate(value);
    if (!parsedDate) {
      return typeof value === "string" ? value : "";
    }
    return formatUtcTimestamp(parsedDate);
  }, [parseTimestampToDate]);

  const formatLastUpdateLabel = useCallback((value) => {
    const parsedDate = parseTimestampToDate(value);
    if (!parsedDate) {
      return typeof value === "string" ? value : "";
    }
    return dayjs(parsedDate).format("YYYY-MM-DD HH:mm");
  }, [parseTimestampToDate]);

  const formatIngestionTimestamp = useCallback((value) => {
    const parsedDate = parseTimestampToDate(value);
    if (!parsedDate) {
      return "";
    }
    return dayjs(parsedDate).format("YYYY-MM-DD HH:mm");
  }, [parseTimestampToDate]);

  const formatDateTimeLocalInput = useCallback((value) => {
    const parsedDate = parseTimestampToDate(value);
    if (!parsedDate) {
      return "";
    }
    return dayjs(parsedDate).format("YYYY-MM-DDTHH:mm");
  }, [parseTimestampToDate]);

  const getIngestionSource = (item) => {
    if (!item) {
      return "";
    }
    const value = item.eventsource || item.file_source || item.file_name || "";
    return typeof value === "string" ? value : String(value);
  };

  const getIngestionTitle = (item) => {
    const source = getIngestionSource(item);
    if (!source) {
      return t('generic_connector.unknown_source', 'Unknown source');
    }
    const parts = source.split(/[\\/]/);
    const lastPart = parts[parts.length - 1];
    return lastPart || source;
  };
  const getIngestionStatus = (item) => {
    if (!item || item.status === undefined || item.status === null) {
      return "";
    }
    return String(item.status).toLowerCase();
  };

  const getIngestionStatusLabel = (status) => {
    if (status === "success") {
      return t('generic_connector.ingestions_status_success', 'Ingested');
    }
    if (status === "skipped") {
      return t('generic_connector.ingestions_status_skipped', 'Skipped');
    }
    if (!status) {
      return t('generic_connector.ingestions_status_unknown', 'Unknown');
    }
    return status;
  };

  const getIngestionStatusColor = (status) => {
    if (status === "success") {
      return "success";
    }
    if (status === "skipped") {
      return "warning";
    }
    return "default";
  };

  // Load available datasources on mount or when dependencies change
  useEffect(() => {
    if (skipInitialDatasourceLoad || !caseid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    requestDatasourceList();
  }, [requestDatasourceList, skipInitialDatasourceLoad]);
  // Handle WebSocket messages
  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      const { command, data } = parsedMessage;
      const handleTokenResult = (payload) => {
        const message = payload || data || parsedMessage || {};
        const rawType = message.type || message.provider || message.connectorType || '';
        const normalizedType = String(rawType).toLowerCase();
        const currentType = String(type || '').toLowerCase();
        if (normalizedType && normalizedType !== currentType) {
          return false;
        }
        const success = message?.result ?? message?.success;
        if (success) {
          setHasError(false);
          setErrorMessage("");
          requestDatasourceList();
          if (ws && caseid && selectedDatasourceId) {
            ws.send(JSON.stringify({
              command: "getGenericSourceConnectorData",
              caseid,
              type,
              datasource_id: selectedDatasourceId
            }));
          }
        } else {
          setHasError(true);
          setErrorMessage(message?.error || "Authorization failed.");
        }
        setLoading(false);
        return true;
      };

      // Special-case: OAuth start messages can come without a `data.type` property
      // so handle them before the generic `data.type !== type` guard so the
      // connector can open the authorization URL when requested by the backend.
      if (command === "authorizeOutlookApiStart" && type === "outlookapi") {
        const url = data?.url || data || parsedMessage?.url || parsedMessage;
        if (url) {
          try {
            window.location.assign(url);
          } catch (err) {
            window.location.href = url;
          }
        } else {
          setHasError(true);
          setErrorMessage(t('generic_connector.outlook_auth_missing_url') || "Authorization URL missing.");
        }
        setLoading(false);
        authWindowRef.current = null;
        return;
      }

      if (command === "authorizeOneDriveApiStart" && type === "OneDrive") {
        const url = data?.url || data || parsedMessage?.url || parsedMessage;
        if (url) {
          try {
            const w = authWindowRef.current;
            if (w && !w.closed) {
              w.location.href = url;
              w.focus();
            } else {
              const nw = window.open(url, '_blank');
              if (nw) nw.focus();
            }
          } catch (err) {
            window.location.href = url;
          }
        } else {
          setHasError(true);
          setErrorMessage(t('generic_connector.onedrive_auth_missing_url') || "Authorization URL missing.");
          if (authWindowRef.current && !authWindowRef.current.closed) {
            authWindowRef.current.close();
            authWindowRef.current = null;
          }
        }
        setLoading(false);
        authWindowRef.current = null;
        return;
      }

      if (command === "authorizeGoogleDriveApiStart" && type === "GoogleDrive") {
        const url = data?.url || data || parsedMessage?.url || parsedMessage;
        if (url) {
          try {
            const w = authWindowRef.current;
            if (w && !w.closed) {
              w.location.href = url;
              w.focus();
            } else {
              const nw = window.open(url, '_blank');
              if (nw) nw.focus();
            }
          } catch (err) {
            window.location.href = url;
          }
        } else {
          setHasError(true);
          setErrorMessage(t('generic_connector.googledrive_auth_missing_url') || "Authorization URL missing.");
          if (authWindowRef.current && !authWindowRef.current.closed) {
            authWindowRef.current.close();
            authWindowRef.current = null;
          }
        }
        setLoading(false);
        authWindowRef.current = null;
        return;
      }

      if (command === "authorizeGmailApiStart" && type === "Gmail") {
        const url = data?.url || data || parsedMessage?.url || parsedMessage;
        if (url) {
          try {
            window.location.assign(url);
          } catch (err) {
            window.location.href = url;
          }
        } else {
          setHasError(true);
          setErrorMessage(t('generic_connector.gmail_auth_missing_url') || "Authorization URL missing.");
          if (authWindowRef.current && !authWindowRef.current.closed) {
            authWindowRef.current.close();
            authWindowRef.current = null;
          }
        }
        setLoading(false);
        authWindowRef.current = null;
        return;
      }

      if (command === GENERIC_PLUGIN_CONNECTOR_OAUTH_COMMANDS.authorizeStart && pluginOAuthConfig) {
        const payload = data || parsedMessage || {};
        const payloadType = String(payload.type || '').trim().toLowerCase();
        if (payloadType && payloadType !== String(type || '').trim().toLowerCase()) {
          return;
        }
        const responseDatasourceId = normalizeDatasourceId(payload.datasourceId || payload.datasource_id);
        if (
          responseDatasourceId &&
          selectedDatasourceId &&
          selectedDatasourceId !== "new" &&
          responseDatasourceId !== selectedDatasourceId
        ) {
          return;
        }
        if (payload.error) {
          clearPluginOAuthAuthorizeTimeout();
          setPluginOAuthAuthorizing(false);
          setPluginOAuthStatus('disconnected');
          setPluginOAuthMessage(payload.error);
          setHasError(true);
          setErrorMessage(payload.error);
          return;
        }
        const authUrl = payload?.url
          || (typeof payload === 'string' ? payload : '')
          || parsedMessage?.url;
        if (authUrl) {
          clearPluginOAuthAuthorizeTimeout();
          try {
            window.location.assign(authUrl);
          } catch (err) {
            window.location.href = authUrl;
          }
          setPluginOAuthStatus('pending');
          setPluginOAuthMessage(t('generic_connector.plugin_oauth.pending') || 'Complete the authentication flow in the opened tab.');
          setHasError(false);
          setErrorMessage('');
        } else {
          clearPluginOAuthAuthorizeTimeout();
          const message = t('generic_connector.plugin_oauth.missing_url') || 'Authorization URL missing.';
          setPluginOAuthStatus('disconnected');
          setPluginOAuthMessage(message);
          setHasError(true);
          setErrorMessage(message);
        }
        setPluginOAuthAuthorizing(false);
        return;
      }

      if (command === GENERIC_PLUGIN_CONNECTOR_OAUTH_COMMANDS.authorizeError && pluginOAuthConfig) {
        const payload = data || parsedMessage || {};
        const payloadType = String(payload.type || '').trim().toLowerCase();
        if (payloadType && payloadType !== String(type || '').trim().toLowerCase()) {
          return;
        }
        clearPluginOAuthAuthorizeTimeout();
        const message = payload.error || 'Authorization failed.';
        setPluginOAuthAuthorizing(false);
        setPluginOAuthStatus('disconnected');
        setPluginOAuthMessage(message);
        setHasError(true);
        setErrorMessage(message);
        return;
      }

      if (command === GENERIC_PLUGIN_CONNECTOR_OAUTH_COMMANDS.tokenResult && pluginOAuthConfig) {
        const payload = data || parsedMessage || {};
        const payloadType = String(payload.type || '').trim().toLowerCase();
        if (payloadType && payloadType !== String(type || '').trim().toLowerCase()) {
          return;
        }
        const responseDatasourceId = normalizeDatasourceId(payload.datasourceId || payload.datasource_id);
        if (
          responseDatasourceId &&
          selectedDatasourceId &&
          selectedDatasourceId !== "new" &&
          responseDatasourceId !== selectedDatasourceId
        ) {
          return;
        }
        clearPluginOAuthAuthorizeTimeout();
        setPluginOAuthAuthorizing(false);
        if (payload.verified) {
          setPluginOAuthVerified(true);
          setPluginOAuthStatus('connected');
          setPluginOAuthMessage(t('generic_connector.plugin_oauth.connected') || 'Connected.');
          setHasError(false);
          setErrorMessage('');
          requestDatasourceList();
          if (ws && selectedDatasourceId && selectedDatasourceId !== "new") {
            ws.send(JSON.stringify({
              command: "getGenericSourceConnectorData",
              caseid,
              type,
              datasource_id: selectedDatasourceId
            }));
          }
        } else {
          const message = payload.error || (t('generic_connector.plugin_oauth.failed') || 'Authentication failed.');
          setPluginOAuthVerified(false);
          setPluginOAuthStatus('disconnected');
          setPluginOAuthMessage(message);
          setHasError(true);
          setErrorMessage(message);
        }
        return;
      }

      if (command === GENERIC_PLUGIN_CONNECTOR_OAUTH_COMMANDS.verifyResult && pluginOAuthConfig) {
        const payload = data || parsedMessage || {};
        const payloadType = String(payload.type || '').trim().toLowerCase();
        if (payloadType && payloadType !== String(type || '').trim().toLowerCase()) {
          return;
        }
        const responseDatasourceId = normalizeDatasourceId(payload.datasourceId || payload.datasource_id);
        if (
          responseDatasourceId &&
          selectedDatasourceId &&
          selectedDatasourceId !== "new" &&
          responseDatasourceId !== selectedDatasourceId
        ) {
          return;
        }
        setPluginOAuthChecking(false);
        if (payload.verified) {
          setPluginOAuthVerified(true);
          setPluginOAuthStatus('connected');
          setPluginOAuthMessage(t('generic_connector.plugin_oauth.connected') || 'Connected.');
        } else {
          setPluginOAuthVerified(false);
          setPluginOAuthStatus('disconnected');
          setPluginOAuthMessage(payload.error || (t('generic_connector.plugin_oauth.invalid') || 'Connection requires re-authentication.'));
        }
        return;
      }

      if (command === "tokenResult") {
        if (handleTokenResult(data || parsedMessage)) {
          return;
        }
      }

      if (command === "gmailTokenResult" && type === "Gmail") {
        const payload = data || parsedMessage || {};
        payload.type = payload.type || "Gmail";
        if (handleTokenResult(payload)) {
          return;
        }
      }

      if (command === "oneDriveTokenResult" && type === "OneDrive") {
        const payload = data || parsedMessage || {};
        payload.type = payload.type || "onedrive";
        if (handleTokenResult(payload)) {
          return;
        }
      }

      if (command === "googleDriveTokenResult" && type === "GoogleDrive") {
        const payload = data || parsedMessage || {};
        payload.type = payload.type || "googledrive";
        if (handleTokenResult(payload)) {
          return;
        }
      }

      if (command === "outlookTokenResult" && type === "outlookapi") {
        const payload = data || parsedMessage || {};
        payload.type = payload.type || "outlookapi";
        if (handleTokenResult(payload)) {
          return;
        }
      }

      if (command === "datasourceIngestionUpdate") {
        const responseDatasourceId = normalizeDatasourceId(data?.datasourceId || data?.datasource_id);
        const matchesDatasource = selectedDatasourceId && selectedDatasourceId !== "new" && responseDatasourceId === selectedDatasourceId;
        const matchesCase = data?.caseid ? String(data.caseid) === String(caseid) : true;
        if (!matchesDatasource || !matchesCase) {
          return;
        }
        const dsId = normalizeDatasourceId(data?.datasource_id || data?.datasourceId);
        if (data?.status === "scanning" && dsId) {
          setScanningDatasourceIds((prev) => new Set(prev).add(dsId));
        } else if (data?.status === "idle" && dsId) {
          setScanningDatasourceIds((prev) => {
            const next = new Set(prev);
            next.delete(dsId);
            return next;
          });
          // Also clear force run loading if the currently selected DS was scanning
          if (dsId === selectedDatasourceId) {
            setForceRunLoading(false);
          }
        }

        const processedCount = Number.isFinite(Number(data?.processed_count))
          ? Number(data.processed_count)
          : null;
        if (processedCount !== null) {
          setLastFoundCount(processedCount);
        }
        const skippedCount = Number.isFinite(Number(data?.skipped_count))
          ? Number(data.skipped_count)
          : null;
        if (skippedCount !== null) {
          setLastSkippedCount(skippedCount);
        }
        const ingestionStatus = String(data?.status || "").toLowerCase();
        const diagnosticMessageRaw = data?.message || data?.diagnostic?.message || "";
        const diagnosticMessage = typeof diagnosticMessageRaw === "string"
          ? diagnosticMessageRaw.trim()
          : String(diagnosticMessageRaw || "").trim();
        const isDiagnosticStatus = ingestionStatus === "error" || ingestionStatus === "warning" || ingestionStatus === "info";
        if (isDiagnosticStatus && diagnosticMessage) {
          if (ingestionStatus === "error") {
            setHasError(true);
            setErrorMessage(diagnosticMessage);
            setForceRunStatus('error');
          } else {
            setForceRunStatus('info');
          }
          setForceRunMessage(diagnosticMessage);
          setIngestionItems((prev) => {
            const existing = prev || [];
            const diagnosticItem = {
              ingested_at: data?.ingested_at || new Date().toISOString(),
              timeevent_id: `diagnostic-${Date.now()}-${Math.random().toString(16).slice(2)}`,
              file_source: diagnosticMessage,
              status: ingestionStatus,
              eventsource: diagnosticMessage
            };
            return [diagnosticItem, ...existing].slice(0, ingestionLimit);
          });
        }
        const timeevent = data?.timeevent || {};
        if (timeevent?.id || timeevent?.eventsource) {
          const nextItem = {
            ingested_at: data?.ingested_at,
            timeevent_id: timeevent.id,
            file_source: timeevent.file_name || timeevent.eventsource,
            status: data?.status || "success",
            eventtime: timeevent.eventtime,
            eventsource: timeevent.eventsource,
            fileModificationDate: timeevent.fileModificationDate
          };
          setIngestionItems((prev) => {
            const existing = prev || [];
            if (nextItem.timeevent_id && existing.some((item) => item.timeevent_id === nextItem.timeevent_id)) {
              return existing;
            }
            const updated = [nextItem, ...existing];
            return updated.slice(0, ingestionLimit);
          });
        }
        return;
      }

      if (!data || data.type !== type) {
        return;
      }

      if (command === "listGenericDatasources") {
        if (data.status === "error") {
          setHasError(true);
          setErrorMessage(data.message || "Unable to load datasources.");
        } else {
          const items = data.datasources || [];
          let nextSelectedDatasourceId = selectedDatasourceId;
          setDatasourceList(items);
          setHasAnyEnabled(items.some((item) => item.enable === 1));
          if (type === "Gmail" || type === "outlookapi" || type === "OneDrive" || type === "GoogleDrive") {
            setHasAnyConnected(items.some((item) => isConnectorConnected(item.dataPreview)));
          } else {
            setHasAnyConnected(false);
          }
          // In dashboard/preload mode we only need list badges/state.
          // Do not auto-select datasource or request full UI while dialog is closed.
          if (!open) {
            setLoading(false);
            return;
          }
          if (selectedDatasourceId && selectedDatasourceId !== "new") {
            const activeDatasource = items.find((item) => item.id === selectedDatasourceId);
            if (activeDatasource) {
              setDatasourceName(activeDatasource.name || '');
              const nextEnabled = activeDatasource.enable === 1;
              setConnectorEnabled(nextEnabled);
              if (initialSettings.current) {
                initialSettings.current = {
                  ...initialSettings.current,
                  enabled: nextEnabled
                };
              }
              const nextFoundCount = Number.isFinite(Number(activeDatasource.lastFoundCount))
                ? Number(activeDatasource.lastFoundCount)
                : 0;
              const nextSkippedCount = Number.isFinite(Number(activeDatasource.lastSkippedCount))
                ? Number(activeDatasource.lastSkippedCount)
                : 0;
              setLastFoundCount(nextFoundCount);
              setLastSkippedCount(nextSkippedCount);
            }
          }
          if (items.length === 0) {
            if (selectedDatasourceId !== "new") {
              setSelectedDatasourceId("new");
            }
            nextSelectedDatasourceId = "new";
            if (!selectedDatasourceId || selectedDatasourceId === "new") {
              setIsStandaloneCreationWizard(true);
            }
            if (!dismissedCreationWizardRef.current) {
              setIsCreationWizardActive(true);
              setCreationWizardStep(0);
            }
            setCreationWizardIngestionMode('later');
            setDatasourceName('');
            setFormValues({});
            if (supportsSinceDate) {
              const formattedDefault = formatLastUpdateValue(defaultSinceDate);
              setLastUpdate(formattedDefault || 0);
              setLastUpdateInput(defaultSinceDate);
            } else {
              setLastUpdate(0);
              setLastUpdateInput(null);
            }
            setLastExecution(0);
            setLastFoundCount(0);
            setLastSkippedCount(0);
            setInterval(NEW_DATASOURCE_DEFAULT_INTERVAL);
            initialSettings.current = {
              formValues: {},
              enabled: false,
              interval: NEW_DATASOURCE_DEFAULT_INTERVAL,
              name: '',
              lastUpdate: supportsSinceDate ? (formatLastUpdateValue(defaultSinceDate) || "") : ""
            };
          } else if (!selectedDatasourceId) {
            setIsStandaloneCreationWizard(false);
            setSelectedDatasourceId(items[0].id);
            nextSelectedDatasourceId = items[0].id;
          } else if (selectedDatasourceId !== "new") {
            const stillExists = items.some((item) => item.id === selectedDatasourceId);
            if (!stillExists) {
              setIsStandaloneCreationWizard(false);
              setSelectedDatasourceId(items[0].id);
              nextSelectedDatasourceId = items[0].id;
            }
          } else {
            setIsStandaloneCreationWizard(false);
          }
          setHasError(false);
          setErrorMessage("");
          const resolvedDatasourceCacheKey = getDatasourceCacheKey(nextSelectedDatasourceId);
          const hasLoadedResolvedDatasource = Boolean(
            resolvedDatasourceCacheKey &&
            lastLoadedDatasourceKeyRef.current === resolvedDatasourceCacheKey
          );
          if (!open || !nextSelectedDatasourceId || hasLoadedResolvedDatasource) {
            setLoading(false);
          }
          return;
        }
        setLoading(false);
        return;
      }

      if (command === "deleteGenericDatasource") {
        if (data.status === "success") {
          const deletedId = normalizeDatasourceId(data.datasourceId);
          if (selectedDatasourceId && selectedDatasourceId !== "new" && deletedId === selectedDatasourceId) {
            setSelectedDatasourceId(null);
          }
          requestDatasourceList();
        } else {
          setHasError(true);
          setErrorMessage(data.error || "Unable to delete datasource.");
        }
        setLoading(false);
        return;
      }

      if (command === "updateDatasourceLastUpdate") {
        const responseDatasourceId = normalizeDatasourceId(data.datasourceId || data.datasource_id);
        if (selectedDatasourceId && selectedDatasourceId !== "new" && responseDatasourceId === selectedDatasourceId) {
          const parsedValue = parseLastUpdateValue(data.lastUpdate);
          setLastUpdate(data.lastUpdate || 0);
          setLastExecution(data.lastExecution || 0);
          setLastUpdateInput(parsedValue);
          setLastUpdateSaving(false);
          setLastUpdateSaved(data.status === "success");
          if (data.status === 'success') {
            setIsEditingLastUpdate(false);
            lastUpdateBackup.current = null;
          }
          if (data.status === "error") {
            setHasError(true);
            setErrorMessage(data.message || "Unable to update last run time.");
          } else {
            setHasError(false);
            setErrorMessage("");
          }
          setTimeout(() => setLastUpdateSaved(false), 2000);
        }
        return;
      }

      if (command === "getGenericSourceConnectorStatus") {
        const responseDatasourceId = normalizeDatasourceId(data.datasourceId);
        if (selectedDatasourceId && selectedDatasourceId !== "new" && responseDatasourceId !== selectedDatasourceId) {
          return;
        }
        if (data.status === "error") {
          setHasError(true);
          setErrorMessage(data.message || "Unable to load connector status.");
          setLoading(false);
          return;
        }
        setConnectorEnabled(data.settings.enable === 1);
        const parsedLastUpdate = parseLastUpdateValue(data.settings.lastUpdate);
        const parsedLastExecution = parseLastUpdateValue(data.settings.lastExecution);
        let resolvedLastUpdateInput = parsedLastUpdate;
        let resolvedLastUpdate = data.settings.lastUpdate || 0;
        if (supportsSinceDate && !parsedLastUpdate && !data.settings.lastUpdate) {
          resolvedLastUpdateInput = defaultSinceDate;
          resolvedLastUpdate = formatLastUpdateValue(defaultSinceDate) || 0;
        }
        const parsedLastFoundCount = Number.isFinite(Number(data.settings?.lastFoundCount))
          ? Number(data.settings.lastFoundCount)
          : 0;
        const parsedLastSkippedCount = Number.isFinite(Number(data.settings?.lastSkippedCount))
          ? Number(data.settings.lastSkippedCount)
          : 0;
        setLastUpdate(resolvedLastUpdate);
        setLastExecution(parsedLastExecution || data.settings.lastExecution || 0);
        setLastUpdateInput(resolvedLastUpdateInput);
        setLastFoundCount(parsedLastFoundCount);
        setLastSkippedCount(parsedLastSkippedCount);
        setInterval(data.settings.interval || 300);
        if (data.datasourceName) {
          setDatasourceName(data.datasourceName);
        }
        setHasError(false);
        setErrorMessage("");
        initialSettings.current = {
          ...(initialSettings.current || {}),
          enabled: data.settings.enable === 1,
          interval: data.settings.interval || 300,
          name: data.datasourceName || datasourceName || '',
          lastUpdate: formatLastUpdateValue(resolvedLastUpdateInput || resolvedLastUpdate)
        };
        const responseDatasourceCacheKey = getDatasourceCacheKey(responseDatasourceId);
        const hasLoadedResponseConnector = Boolean(
          responseDatasourceCacheKey &&
          lastLoadedDatasourceKeyRef.current === responseDatasourceCacheKey
        );
        if (hasLoadedResponseConnector) {
          setLoading(false);
        }
        return;
      }

      if (command === "getDatasourceIngestions") {
        const responseDatasourceId = normalizeDatasourceId(data.datasourceId || data.datasource_id);
        if (selectedDatasourceId && selectedDatasourceId !== "new" && responseDatasourceId !== selectedDatasourceId) {
          return;
        }
        if (data.status === "error") {
          setIngestionsError(data.message || "Unable to load ingestions.");
          setIngestionsLoading(false);
          return;
        }
        setIngestionItems(Array.isArray(data.ingestions) ? data.ingestions : []);
        setIngestionsError("");
        setIngestionsLoading(false);
        return;
      }

      if (command === "getGenericSourceConnectorData") {
        if (data.status === "error") {
          setHasError(true);
          setErrorMessage(data.message || "Unable to load connector form.");
          setLoading(false);
          return;
        }
        if (!data.data) {
          setHasError(true);
          setErrorMessage("No connector UI definition received.");
          setLoading(false);
          return;
        }
        const responseDatasourceId = normalizeDatasourceId(data.datasourceId);
        const matchesSelection =
          (selectedDatasourceId === "new" && !responseDatasourceId) ||
          (!selectedDatasourceId && !responseDatasourceId) ||
          (selectedDatasourceId && selectedDatasourceId !== "new" && responseDatasourceId === selectedDatasourceId);
        if (!matchesSelection) {
          return;
        }

        if (!data.data.components) {
          data.data.components = [];
        }

        const hasPluginOAuthInPayload = Object.prototype.hasOwnProperty.call(data, 'pluginOAuth');
        const payloadPluginOAuth = hasPluginOAuthInPayload && data.pluginOAuth && typeof data.pluginOAuth === 'object'
          ? data.pluginOAuth
          : null;
        if (hasPluginOAuthInPayload) {
          setRuntimePluginOAuth(payloadPluginOAuth);
        }

        const hasPluginMemoryInPayload = Object.prototype.hasOwnProperty.call(data, 'pluginMemory');
        const payloadPluginMemory = hasPluginMemoryInPayload && data.pluginMemory && typeof data.pluginMemory === 'object'
          ? data.pluginMemory
          : null;
        if (hasPluginMemoryInPayload) {
          setRuntimePluginMemory(payloadPluginMemory);
        }

        const uiData = data.data;
        lastLoadedDatasourceKeyRef.current = getDatasourceCacheKey(
          responseDatasourceId ?? (selectedDatasourceId === "new" ? "new" : selectedDatasourceId)
        );
        setConnectorUI(uiData);
        const savedData = data.savedData || {};
        const newFormValues = { ...savedData };
        const payloadPluginOAuthConfig = normalizeConnectorPluginOAuthConfig(payloadPluginOAuth);
        const activePluginOAuthConfig = payloadPluginOAuthConfig || pluginOAuthConfig;

        if (uiData.components) {
          uiData.components.forEach((component) => {
            if (component.name && !(component.name in newFormValues) && component.value) {
              newFormValues[component.name] = component.value;
            }
          });
        }

        setFormValues(newFormValues);
        if (activePluginOAuthConfig) {
          const pluginAuth = (savedData && typeof savedData === 'object')
            ? (savedData[activePluginOAuthConfig.authKey] || {})
            : {};
          const accountFromAuth = typeof pluginAuth.account === 'string' ? pluginAuth.account : '';
          const authOauthConfig = (pluginAuth && typeof pluginAuth === 'object' && pluginAuth.oauthConfig && typeof pluginAuth.oauthConfig === 'object')
            ? pluginAuth.oauthConfig
            : {};
          const readOverride = (camelKey, snakeKey, fallbackValue = '') => {
            const rawValue = authOauthConfig[camelKey] ?? authOauthConfig[snakeKey] ?? fallbackValue;
            return typeof rawValue === 'string' ? rawValue : '';
          };
          setPluginOAuthAccount(accountFromAuth);
          setPluginOAuthOverrides({
            authorizeUrl: readOverride('authorizeUrl', 'authorize_url', activePluginOAuthConfig.authorizeUrl || ''),
            tokenUrl: readOverride('tokenUrl', 'token_url', activePluginOAuthConfig.tokenUrl || ''),
            clientId: readOverride('clientId', 'client_id', activePluginOAuthConfig.clientId || ''),
            clientIdEnv: readOverride('clientIdEnv', 'client_id_env', activePluginOAuthConfig.clientIdEnv || ''),
            clientSecret: readOverride('clientSecret', 'client_secret', activePluginOAuthConfig.clientSecret || ''),
            clientSecretEnv: readOverride('clientSecretEnv', 'client_secret_env', activePluginOAuthConfig.clientSecretEnv || ''),
          });
          setPluginOAuthVerified(Boolean(pluginAuth.verified));
          setPluginOAuthAuthorizing(false);
          setPluginOAuthChecking(false);
          if (pluginAuth.pendingState) {
            setPluginOAuthStatus('pending');
            setPluginOAuthMessage(t('generic_connector.plugin_oauth.pending') || 'Complete the authentication flow in the opened tab.');
          } else if (pluginAuth.verified) {
            setPluginOAuthStatus('connected');
            setPluginOAuthMessage(t('generic_connector.plugin_oauth.connected') || 'Connected.');
          } else {
            setPluginOAuthStatus('disconnected');
            setPluginOAuthMessage('');
          }
        }
        if (data.datasourceName) {
          setDatasourceName(data.datasourceName);
        }
        initialSettings.current = {
          ...(initialSettings.current || {}),
          formValues: newFormValues,
          name: data.datasourceName || datasourceName || ''
        };
        setIsSaveDisabled(true);
        setHasError(false);
        setErrorMessage("");
        setLoading(false);
        return;
      }

      if (command === "saveGenericDatasource") {
        const responseDatasourceId = normalizeDatasourceId(data.datasourceId || data.datasource_id);
        if (data.status === "success") {
          const pendingWizardAction = pendingWizardActionRef.current;
          pendingWizardActionRef.current = null;
          setSaveSuccess(true);
          const savedId = normalizeDatasourceId(data.datasourceId || data.datasource_id);
          if (savedId) {
            setSelectedDatasourceId(savedId);
          }
          // Clear inline saving state if applicable and provide a brief success indicator
          if (savingNameId) {
            const _savedId = savedId || savingNameId;
            setSavingNameId(null);
            setSavedNameSuccessId(_savedId);
            setEditingNameId(null);
            setEditingNameValue('');
            setTimeout(() => setSavedNameSuccessId(null), 2000);
          }
          setTimeout(() => setSaveSuccess(false), 2000);
          const resolvedEnabled = typeof pendingWizardAction?.enableOverride === 'boolean'
            ? pendingWizardAction.enableOverride
            : connectorEnabled;
          setConnectorEnabled(resolvedEnabled);
          initialSettings.current = {
            formValues,
            enabled: resolvedEnabled,
            interval,
            name: datasourceName || '',
            lastUpdate: supportsSinceDate ? (formatLastUpdateValue(lastUpdateInput || lastUpdate) || "") : (initialSettings.current?.lastUpdate || "")
          };
          setIsSaveDisabled(true);
          if (pendingWizardAction?.type === 'advance') {
            setIsCreationWizardActive(true);
            setCreationWizardStep(pendingWizardAction.step);
          } else if (pendingWizardAction?.type === 'finish') {
            if (typeof onDatasourceConfigured === 'function') {
              onDatasourceConfigured({
                caseid,
                type,
                title,
                datasourceId: savedId || responseDatasourceId || selectedDatasourceId || null
              });
            }
            setIsStandaloneCreationWizard(false);
            dismissedCreationWizardRef.current = false;
            setIsCreationWizardActive(false);
            setCreationWizardStep(0);
            setCreationWizardIngestionMode('later');
            if (pendingWizardAction?.forceRunAfterSave && !projectCreationWizardMode) {
              handleForceRun(savedId || responseDatasourceId || selectedDatasourceId);
            }
          } else if (typeof onDatasourceConfigured === 'function') {
            onDatasourceConfigured({
              caseid,
              type,
              title,
              datasourceId: savedId || responseDatasourceId || selectedDatasourceId || null
            });
          }
          if (responseDatasourceId) {
            setTogglingDatasourceIds((prev) => {
              if (!prev.has(responseDatasourceId)) {
                return prev;
              }
              const next = new Set(prev);
              next.delete(responseDatasourceId);
              return next;
            });
          }
          requestDatasourceList();
        } else {
          // Clear per-card saving indicator on error
          if (savingNameId) {
            setSavingNameId(null);
          }
          if (responseDatasourceId) {
            setTogglingDatasourceIds((prev) => {
              if (!prev.has(responseDatasourceId)) {
                return prev;
              }
              const next = new Set(prev);
              next.delete(responseDatasourceId);
              return next;
            });
          }
          pendingWizardActionRef.current = null;
          setHasError(true);
          setErrorMessage(data.error || "Unable to save datasource.");
        }
        setLoading(false);
        return;
      }

      if (command === "authorizeOutlookApiStart" && type === "outlookapi") {
        const url = data.url || data;
        if (url) {
          window.location.href = url;
        } else {
          setHasError(true);
          setErrorMessage(t('generic_connector.outlook_auth_missing_url') || "Authorization URL missing.");
        }
        setLoading(false);
        return;
      }

      if (command === "authorizeGmailApiStart" && type === "Gmail") {
        const url = data.url || data;
        if (url) {
          window.location.href = url;
        } else {
          setHasError(true);
          setErrorMessage(t('generic_connector.gmail_auth_missing_url') || "Authorization URL missing.");
        }
        setLoading(false);
        return;
      }

      if (command === "authorizeOneDriveApiStart" && type === "OneDrive") {
        const url = data.url || data;
        if (url) {
          window.location.href = url;
        } else {
          setHasError(true);
          setErrorMessage(t('generic_connector.onedrive_auth_missing_url') || "Authorization URL missing.");
        }
        setLoading(false);
        return;
      }

      if (command === "authorizeGoogleDriveApiStart" && type === "GoogleDrive") {
        const url = data.url || data;
        if (url) {
          window.location.href = url;
        } else {
          setHasError(true);
          setErrorMessage(t('generic_connector.googledrive_auth_missing_url') || "Authorization URL missing.");
        }
        setLoading(false);
        return;
      }

      // Handle Gmail OAuth errors
      if (command === "authorizeGmailApiError" && type === "Gmail") {
        setHasError(true);
        setErrorMessage(data?.error || parsedMessage?.error || "Gmail authorization failed.");
        setLoading(false);
        // Close the popup window if it was opened
        if (authWindowRef.current && !authWindowRef.current.closed) {
          authWindowRef.current.close();
          authWindowRef.current = null;
        }
        return;
      }

      if (command === "authorizeOneDriveApiError" && type === "OneDrive") {
        setHasError(true);
        setErrorMessage(data?.error || parsedMessage?.error || "OneDrive authorization failed.");
        setLoading(false);
        if (authWindowRef.current && !authWindowRef.current.closed) {
          authWindowRef.current.close();
          authWindowRef.current = null;
        }
        return;
      }

      if (command === "authorizeGoogleDriveApiError" && type === "GoogleDrive") {
        setHasError(true);
        setErrorMessage(data?.error || parsedMessage?.error || "Google Drive authorization failed.");
        setLoading(false);
        if (authWindowRef.current && !authWindowRef.current.closed) {
          authWindowRef.current.close();
          authWindowRef.current = null;
        }
        return;
      }



      if (command === "forceRunDatasource") {
        const payload = data || parsedMessage || {};
        const responseDatasourceId = normalizeDatasourceId(payload.datasourceId || payload.datasource_id);
        if (
          responseDatasourceId &&
          selectedDatasourceId &&
          selectedDatasourceId !== "new" &&
          responseDatasourceId !== selectedDatasourceId
        ) {
          return;
        }

        if (payload.status === "success") {
          // Keep loading true until completion message arrives.
          setForceRunStatus('info');
          setForceRunMessage(t('generic_connector.force_run_queued') || 'Datasource queued. Waiting for completion...');
          startForceRunTimeout();
          setHasError(false);
          setErrorMessage("");
        } else {
          clearForceRunTimeout();
          setHasError(true);
          const failureMessage = payload.message || "Failed to force run datasource.";
          setErrorMessage(failureMessage);
          setForceRunStatus('error');
          setForceRunMessage(failureMessage);
          setForceRunLoading(false);
        }
        return;
      }

      if (command === "forceRunDatasourceComplete") {
        const payload = data || parsedMessage || {};
        const responseDatasourceId = normalizeDatasourceId(payload.datasourceId || payload.datasource_id);

        if (selectedDatasourceId && selectedDatasourceId !== "new" && String(responseDatasourceId) === String(selectedDatasourceId)) {
          clearForceRunTimeout();
          if (payload.status === "success") {
            setForceRunStatus('success');
            setForceRunMessage(t('generic_connector.force_run_completed') || 'Datasource ingestion completed.');
            // Force run completed successfully, refresh connector status
            ws.send(JSON.stringify({
              command: "getGenericSourceConnectorStatus",
              caseid,
              type,
              datasource_id: selectedDatasourceId
            }));
            requestDatasourceList();
          } else {
            const failureMessage = payload.message || "Force run failed.";
            setHasError(true);
            setErrorMessage(failureMessage);
            setForceRunStatus('error');
            setForceRunMessage(failureMessage);
          }
          setForceRunLoading(false);
        }
        return;
      }
    });

    return unsubscribe;
  }, [subscribeToMessages, type, selectedDatasourceId, requestDatasourceList, formValues, connectorEnabled, interval, datasourceName, parseLastUpdateValue, formatLastUpdateValue, caseid, savingNameId, t, ws, supportsSinceDate, defaultSinceDate, open, pluginOAuthConfig, startForceRunTimeout, clearForceRunTimeout, getDatasourceCacheKey, projectCreationWizardMode]);

  useEffect(() => {
    setIngestionsOpen(false);
    setIngestionsLoading(false);
    setIngestionsError("");
    setIngestionItems([]);
    setIngestionFilter("all");
    setForceRunLoading(false);
    setForceRunStatus('');
    setForceRunMessage('');
    clearForceRunTimeout();
  }, [selectedDatasourceId, clearForceRunTimeout]);

  // Fetch connector status and data when selection changes
  useEffect(() => {
    if (!open || !ws || !selectedDatasourceId) {
      return;
    }
    if (!caseid) {
      if (!dismissedCreationWizardRef.current) {
        setIsCreationWizardActive(true);
        setCreationWizardStep(0);
      }
      setCreationWizardIngestionMode('later');
      setConnectorEnabled(false);
      if (supportsSinceDate) {
        const formattedDefault = formatLastUpdateValue(defaultSinceDate);
        setLastUpdate(formattedDefault || 0);
        setLastUpdateInput(defaultSinceDate);
      } else {
        setLastUpdate(0);
        setLastUpdateInput(null);
      }
      setLastFoundCount(0);
      setLastSkippedCount(0);
      setInterval(NEW_DATASOURCE_DEFAULT_INTERVAL);
      setDatasourceName('');
      setFormValues({});
      setDatasourceList([]);
      setPluginOAuthAccount('');
      setPluginOAuthOverrides({
        authorizeUrl: '',
        tokenUrl: '',
        clientId: '',
        clientIdEnv: '',
        clientSecret: '',
        clientSecretEnv: '',
      });
      setPluginOAuthStatus('disconnected');
      setPluginOAuthVerified(false);
      setPluginOAuthMessage('');
      setPluginOAuthAuthorizing(false);
      setPluginOAuthChecking(false);
      initialSettings.current = {
        formValues: {},
        enabled: false,
        interval: NEW_DATASOURCE_DEFAULT_INTERVAL,
        name: '',
        lastUpdate: supportsSinceDate ? (formatLastUpdateValue(defaultSinceDate) || "") : ""
      };
      setLoading(false);
      return;
    }
    const nextDatasourceCacheKey = getDatasourceCacheKey(selectedDatasourceId);
    const hasCachedConnectorConfig = Boolean(
      nextDatasourceCacheKey &&
      lastLoadedDatasourceKeyRef.current === nextDatasourceCacheKey
    );
    // cancel any inline edit on selection change
    setIsEditingLastUpdate(false);
    setEditingNameId(null);
    setEditingNameValue('');
    if (!hasCachedConnectorConfig) {
      setLoading(true);
    }
    if (selectedDatasourceId === "new") {
      if (!dismissedCreationWizardRef.current) {
        setIsCreationWizardActive(true);
        setCreationWizardStep(0);
      }
      setCreationWizardIngestionMode('later');
      setConnectorEnabled(false);
      if (supportsSinceDate) {
        const formattedDefault = formatLastUpdateValue(defaultSinceDate);
        setLastUpdate(formattedDefault || 0);
        setLastUpdateInput(defaultSinceDate);
      } else {
        setLastUpdate(0);
        setLastUpdateInput(null);
      }
      setLastFoundCount(0);
      setLastSkippedCount(0);
      setInterval(NEW_DATASOURCE_DEFAULT_INTERVAL);
      setDatasourceName('');
      setFormValues({});
      setPluginOAuthAccount('');
      setPluginOAuthOverrides({
        authorizeUrl: '',
        tokenUrl: '',
        clientId: '',
        clientIdEnv: '',
        clientSecret: '',
        clientSecretEnv: '',
      });
      setPluginOAuthStatus('disconnected');
      setPluginOAuthVerified(false);
      setPluginOAuthMessage('');
      setPluginOAuthAuthorizing(false);
      setPluginOAuthChecking(false);
      initialSettings.current = {
        formValues: {},
        enabled: false,
        interval: NEW_DATASOURCE_DEFAULT_INTERVAL,
        name: '',
        lastUpdate: supportsSinceDate ? (formatLastUpdateValue(defaultSinceDate) || "") : ""
      };
      ws.send(JSON.stringify({
        command: "getGenericSourceConnectorData",
        caseid,
        type,
        force_new: true
      }));
      return;
    }

    ws.send(JSON.stringify({
      command: "getGenericSourceConnectorStatus",
      caseid,
      type,
      datasource_id: selectedDatasourceId
    }));
    ws.send(JSON.stringify({
      command: "getGenericSourceConnectorData",
      caseid,
      type,
      datasource_id: selectedDatasourceId
    }));
  }, [open, ws, caseid, type, selectedDatasourceId, supportsSinceDate, defaultSinceDate, formatLastUpdateValue, getDatasourceCacheKey]);

  useEffect(() => {
    if (!pluginOAuthConfig || !ws || !open || !selectedDatasourceId || selectedDatasourceId === "new") {
      return;
    }
    setPluginOAuthChecking(true);
    setPluginOAuthStatus('pending');
    setPluginOAuthMessage(t('generic_connector.plugin_oauth.checking') || 'Checking OAuth connection...');
    ws.send(JSON.stringify({
      command: pluginOAuthConfig.commands.verify,
      caseid,
      datasourceId: selectedDatasourceId,
      type,
      provider: pluginOAuthConfig.provider,
    }));
  }, [pluginOAuthConfig, ws, open, selectedDatasourceId, caseid, type, t]);

  // Check for unsaved changes
  useEffect(() => {
    if (!initialSettings.current) {
      return;
    }
    const baselineFormValues = initialSettings.current.formValues || {};
    const combinedKeys = new Set([...Object.keys(baselineFormValues), ...Object.keys(formValues)]);
    const formChanged = Array.from(combinedKeys).some((key) => formValues[key] !== baselineFormValues[key]);
    const hasToggleChanged = (initialSettings.current.enabled ?? false) !== connectorEnabled;
    const hasIntervalChanged = (initialSettings.current.interval ?? 300) !== interval;
    const hasNameChanged = (initialSettings.current.name || '') !== (datasourceName || '');
    const baselineLastUpdate = initialSettings.current.lastUpdate || '';
    const currentLastUpdate = supportsSinceDate ? (formatLastUpdateValue(lastUpdateInput || lastUpdate) || '') : baselineLastUpdate;
    const hasLastUpdateChanged = supportsSinceDate && baselineLastUpdate !== currentLastUpdate;
    setIsSaveDisabled(!formChanged && !hasToggleChanged && !hasIntervalChanged && !hasNameChanged && !hasLastUpdateChanged);
  }, [connectorEnabled, formValues, interval, datasourceName, supportsSinceDate, lastUpdateInput, lastUpdate, formatLastUpdateValue]);
  const normalizedLastFoundCount = Number.isFinite(Number(lastFoundCount)) ? Number(lastFoundCount) : 0;
  const normalizedLastSkippedCount = Number.isFinite(Number(lastSkippedCount)) ? Number(lastSkippedCount) : 0;
  const lastFoundLabel = t('generic_connector.new_events_found', {
    count: normalizedLastFoundCount,
    defaultValue: `${normalizedLastFoundCount} new events`
  });
  const lastSkippedLabel = t('generic_connector.skipped_events_found', {
    count: normalizedLastSkippedCount,
    defaultValue: `${normalizedLastSkippedCount} skipped`
  });
  const filteredIngestionItems = ingestionFilter === "all"
    ? ingestionItems
    : ingestionItems.filter((item) => getIngestionStatus(item) === ingestionFilter);
  const handleConnectorToggle = (enabled) => {
    setConnectorEnabled(enabled);
    setIsSaveDisabled(false);
  };
  // Handle opening the dialog - get connector UI definition when dialog is opened
  const handleOpen = async () => {
    if (typeof beforeOpen === 'function') {
      const shouldContinue = await beforeOpen({ caseid, type, title });
      if (shouldContinue === false) {
        return;
      }
    }
    const currentDatasourceCacheKey = getDatasourceCacheKey(selectedDatasourceId);
    const hasCachedConnectorConfig = Boolean(
      currentDatasourceCacheKey &&
      lastLoadedDatasourceKeyRef.current === currentDatasourceCacheKey
    );
    setOpen(true);
    setIsStandaloneCreationWizard(false);
    setShowPluginGuide(false);
    if (!hasCachedConnectorConfig) {
      setLoading(true);
    }
    if (!caseid) {
      setSelectedDatasourceId("new");
      setLoading(false);
      if (typeof onOpen === 'function') {
        onOpen();
      }
      return;
    }
    requestDatasourceList();
    if (typeof onOpen === 'function') {
      onOpen();
    }
  };

  const handleClose = () => {
    setOpen(false);
    setShowPluginGuide(false);
    setIsGuideDialogOpen(false);
    setIngestionsOpen(false);
    setIsStandaloneCreationWizard(false);
    dismissedCreationWizardRef.current = false;
    setIsCreationWizardActive(false);
    setCreationWizardStep(0);
    setCreationWizardIngestionMode('later');
    pendingWizardActionRef.current = null;
  };
  const handleCloseCreationWizard = () => {
    if (isStandaloneCreationWizard) {
      handleClose();
      return;
    }
    dismissedCreationWizardRef.current = true;
    setIsCreationWizardActive(false);
    setCreationWizardStep(0);
    setCreationWizardIngestionMode('later');
    pendingWizardActionRef.current = null;
  };

  const handleCloseConnectorGuide = useCallback(() => {
    setIsGuideDialogOpen(false);
  }, []);

  const handleOpenConnectorGuide = useCallback(() => {
    if (!hasConnectorGuide || !normalizedGuideUrl) {
      return;
    }
    setIsGuideDialogOpen(true);
  }, [hasConnectorGuide, normalizedGuideUrl]);

  useEffect(() => {
    if (forceOpen && !open) {
      handleOpen();
    }
  }, [forceOpen, open]);

  const handleFieldChange = (field, value) => {
    setFormValues(prev => ({
      ...prev,
      [field]: value
    }));
    setIsSaveDisabled(false);
  };

  const handlePluginOAuthOverrideChange = (field, value) => {
    setPluginOAuthOverrides((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsSaveDisabled(false);
  };

  const handleLastUpdateChange = (newValue) => {
    setLastUpdateInput(newValue);
    const formatted = formatLastUpdateValue(newValue);
    setLastUpdate(formatted || 0);
  };

  const handleSinceDateChange = (newValue) => {
    handleLastUpdateChange(newValue);
    setIsSaveDisabled(false);
  };

  const handleStartEditLastUpdate = () => {
    lastUpdateBackup.current = lastUpdate;
    const parsed = parseLastUpdateValue(lastUpdate);
    setLastUpdateInput(parsed);
    setIsEditingLastUpdate(true);
  };

  const isConnectorConnected = useCallback((preview) => {
    if (!preview || typeof preview !== "object") {
      return false;
    }
    const hasValue = (obj, key) => (
      Object.prototype.hasOwnProperty.call(obj, key) &&
      obj[key] !== null &&
      obj[key] !== undefined &&
      obj[key] !== ""
    );
    if (hasValue(preview, "access_token") || hasValue(preview, "refresh_token")) {
      return true;
    }
    if (pluginOAuthConfig?.authKey) {
      const pluginAuth = preview[pluginOAuthConfig.authKey];
      if (pluginAuth && typeof pluginAuth === 'object') {
        if (pluginAuth.verified === true) {
          return true;
        }
        if (hasValue(pluginAuth, "access_token") || hasValue(pluginAuth, "refresh_token")) {
          return true;
        }
        const pluginToken = pluginAuth.token;
        if (pluginToken && typeof pluginToken === "object") {
          return Boolean(
            hasValue(pluginToken, "access_token") ||
            hasValue(pluginToken, "refresh_token")
          );
        }
      }
    }
    const token = preview.token;
    if (token && typeof token === "object") {
      return Boolean(
        hasValue(token, "access_token") ||
        hasValue(token, "refresh_token")
      );
    }
    return false;
  }, [pluginOAuthConfig]);

  const connectorConnected = useMemo(
    () => isConnectorConnected(formValues),
    [isConnectorConnected, formValues]
  );

  const handleCancelEditLastUpdate = () => {
    setLastUpdateInput(parseLastUpdateValue(lastUpdateBackup.current));
    setLastUpdate(lastUpdateBackup.current || 0);
    setLastUpdateSaved(false);
    setLastUpdateSaving(false);
    setIsEditingLastUpdate(false);
    lastUpdateBackup.current = null;
  };

  const handleLastUpdateSave = () => {
    if (!ws || !selectedDatasourceId || selectedDatasourceId === "new") {
      return;
    }
    const formatted = formatLastUpdateValue(lastUpdateInput || lastUpdate);
    setLastUpdateSaving(true);
    setLastUpdateSaved(false);
    ws.send(JSON.stringify({
      command: "updateDatasourceLastUpdate",
      caseid,
      datasource_id: selectedDatasourceId,
      type,
      lastUpdate: formatted
    }));
  };

  const handleResetLastUpdate = () => {
    handleLastUpdateChange(null);
    setIsSaveDisabled(false);

    if (!ws || !selectedDatasourceId || selectedDatasourceId === "new") {
      return;
    }

    setLastUpdateSaving(true);
    setLastUpdateSaved(false);
    ws.send(JSON.stringify({
      command: "updateDatasourceLastUpdate",
      caseid,
      datasource_id: selectedDatasourceId,
      type,
      lastUpdate: ""
    }));
  };

  const handleSelectDatasource = (value) => {
    if (value === "new") {
      setIsStandaloneCreationWizard(false);
      dismissedCreationWizardRef.current = false;
      pendingWizardActionRef.current = null;
      setIsCreationWizardActive(true);
      setCreationWizardStep(0);
      setCreationWizardIngestionMode('later');
      setSelectedDatasourceId("new");
      setDatasourceName('');
      setInterval(NEW_DATASOURCE_DEFAULT_INTERVAL);
      if (supportsSinceDate && !lastUpdateInput && !lastUpdate) {
        handleLastUpdateChange(defaultSinceDate);
      }
      return;
    }
    const normalized = normalizeDatasourceId(value);
    if (normalized === null) {
      return;
    }
    setIsStandaloneCreationWizard(false);
    dismissedCreationWizardRef.current = false;
    pendingWizardActionRef.current = null;
    setIsCreationWizardActive(false);
    setCreationWizardStep(0);
    setCreationWizardIngestionMode('later');
    setSelectedDatasourceId(normalized);
  };

  const handleQuickToggleEnable = (event, ds) => {
    // toggles enable status directly from the card
    event.stopPropagation();
    if (!ws || !ds || !ds.id) return;
    if (togglingDatasourceIds.has(ds.id)) {
      return;
    }
    const newEnable = ds.enable === 1 ? 0 : 1;
    const payload = {
      command: 'saveGenericDatasource',
      caseid,
      type,
      datasource_id: ds.id,
      enable: newEnable,
    };
    // Preserve existing settings when toggling from the list.
    if (ds.dataPreview && typeof ds.dataPreview === 'object' && Object.keys(ds.dataPreview).length > 0) {
      payload.data = ds.dataPreview;
    }
    setTogglingDatasourceIds((prev) => new Set(prev).add(ds.id));
    ws.send(JSON.stringify(payload));
  };

  // Inline editing helpers for datasource names
  const handleStartEditName = (event, ds) => {
    event.stopPropagation();
    setEditingNameId(ds.id);
    setEditingNameValue(ds.name || '');
  };

  const handleCancelEditName = (event) => {
    event.stopPropagation();
    setEditingNameId(null);
    setEditingNameValue('');
  };

  const handleSaveEditName = (event, ds) => {
    event.stopPropagation();
    if (!ws || !ds || !ds.id) return;
    const nameTrim = (editingNameValue || '').trim();
    // Nothing changed or empty - just cancel edit
    if (!nameTrim || nameTrim === (ds.name || '')) {
      setEditingNameId(null);
      setEditingNameValue('');
      return;
    }
    setSavingNameId(ds.id);
    // Use the same save command as the settings form - server will update the name
    const payload = {
      command: 'saveGenericDatasource',
      caseid,
      type,
      datasource_id: ds.id,
      name: nameTrim,
    };
    if (ds.dataPreview && typeof ds.dataPreview === 'object' && Object.keys(ds.dataPreview).length > 0) {
      payload.data = ds.dataPreview;
    }
    ws.send(JSON.stringify(payload));
  };



  const handleSaveSettings = async (options = {}) => {
    const { enableOverride = null, pendingWizardAction = null } = options;
    setLoading(true);
    if (ws) {
      if (pendingWizardAction) {
        pendingWizardActionRef.current = pendingWizardAction;
      }
      let targetCaseId = caseid;
      if (!targetCaseId && typeof ensureCase === 'function') {
        try {
          targetCaseId = await ensureCase({ type, title });
        } catch (error) {
          setLoading(false);
          pendingWizardActionRef.current = null;
          setHasError(true);
          setErrorMessage(error?.message || "Unable to create temporary project.");
          return;
        }
      }
      if (!targetCaseId) {
        setLoading(false);
        pendingWizardActionRef.current = null;
        setHasError(true);
        setErrorMessage("Unable to create temporary project.");
        return;
      }
      const parsedInterval = parseInt(interval, 10);
      const resolvedEnabled = typeof enableOverride === 'boolean' ? enableOverride : connectorEnabled;
      let nextFormValues = formValues;
      if (pluginOAuthConfig && pluginOAuthRuntimeIsGeneric) {
        const oauthConfigPayload = buildPluginOAuthConfigPayload();
        if (Object.keys(oauthConfigPayload).length > 0) {
          const existingAuthRecord = (formValues?.[pluginOAuthConfig.authKey] && typeof formValues[pluginOAuthConfig.authKey] === 'object')
            ? formValues[pluginOAuthConfig.authKey]
            : {};
          nextFormValues = {
            ...formValues,
            [pluginOAuthConfig.authKey]: {
              ...existingAuthRecord,
              oauthConfig: oauthConfigPayload,
            },
          };
        }
      }
      const payload = {
        command: "saveGenericDatasource",
        caseid: targetCaseId,
        type,
        data: nextFormValues,
        enable: resolvedEnabled ? 1 : 0,
        interval: parsedInterval >= 300 ? parsedInterval : 300
      };
      if (supportsSinceDate) {
        const formattedLastUpdate = formatLastUpdateValue(lastUpdateInput || lastUpdate);
        const resolvedLastUpdate = formattedLastUpdate || formatLastUpdateValue(defaultSinceDate);
        if (resolvedLastUpdate) {
          payload.lastUpdate = resolvedLastUpdate;
        }
      }
      if (selectedDatasourceId && selectedDatasourceId !== "new") {
        payload.datasource_id = selectedDatasourceId;
      } else {
        payload.createNew = true;
      }
      if (datasourceName && datasourceName.trim().length > 0) {
        payload.name = datasourceName.trim();
      }
      ws.send(JSON.stringify(payload));
    }
  };
  const handleCreationWizardBack = () => {
    setCreationWizardStep((previousStep) => getPreviousCreationWizardStep(previousStep));
  };
  const handleCreationWizardNext = () => {
    if (creationWizardStep === 0) {
      pendingWizardActionRef.current = {
        type: 'advance',
        step: getNextCreationWizardStep(0),
      };
      handleSaveSettings();
      return;
    }
    if (creationWizardStep === 1) {
      setCreationWizardStep(getNextCreationWizardStep(1));
      return;
    }
    if (creationWizardStep === 2) {
      pendingWizardActionRef.current = {
        type: 'advance',
        step: getNextCreationWizardStep(2),
      };
      handleSaveSettings();
    }
  };
  const handleCreationWizardComplete = () => {
    if (creationWizardIngestionMode === 'now') {
      handleSaveSettings({
        enableOverride: true,
        pendingWizardAction: {
          type: 'finish',
          enableOverride: true,
          forceRunAfterSave: true
        }
      });
      return;
    }
    dismissedCreationWizardRef.current = false;
    setIsCreationWizardActive(false);
    setCreationWizardStep(0);
    setCreationWizardIngestionMode('later');
    pendingWizardActionRef.current = null;
    if (typeof onDatasourceConfigured === 'function') {
      onDatasourceConfigured({
        caseid,
        type,
        title,
        datasourceId: selectedDatasourceId && selectedDatasourceId !== "new" ? selectedDatasourceId : null
      });
    }
  };

  const handlePluginConnectorOAuthAuthorize = () => {
    if (!pluginOAuthConfig) {
      return;
    }
    const oauthConfigPayload = buildPluginOAuthConfigPayload();
    const wsOpenState = typeof WebSocket !== 'undefined' ? WebSocket.OPEN : 1;
    if (!ws || ws.readyState !== wsOpenState) {
      const message = t('generic_connector.plugin_oauth.ws_not_connected') || 'Server connection is not ready. Reconnect and try again.';
      setPluginOAuthStatus('disconnected');
      setPluginOAuthMessage(message);
      setHasError(true);
      setErrorMessage(message);
      return;
    }
    if (!selectedDatasourceId || selectedDatasourceId === "new") {
      const message = t('generic_connector.plugin_oauth.save_before_connect') || "Save this datasource before connecting.";
      setPluginOAuthStatus('disconnected');
      setPluginOAuthMessage(message);
      setHasError(true);
      setErrorMessage(message);
      return;
    }
    if (pluginOAuthConfig.accountRequired && !pluginOAuthAccountValue) {
      const message = t('generic_connector.plugin_oauth.account_required', {
        provider: pluginOAuthConfig.label,
        defaultValue: `Enter a ${pluginOAuthConfig.label} account before connecting.`,
      });
      setPluginOAuthStatus('disconnected');
      setPluginOAuthMessage(message);
      setHasError(true);
      setErrorMessage(message);
      return;
    }
    if (pluginOAuthRuntimeIsGeneric && pluginOAuthMissingRequiredFields.length > 0) {
      const message = t('generic_connector.plugin_oauth.missing_required', {
        fields: pluginOAuthMissingRequiredFields.join(', '),
        defaultValue: `Complete OAuth fields before connecting: ${pluginOAuthMissingRequiredFields.join(', ')}`,
      });
      setPluginOAuthStatus('disconnected');
      setPluginOAuthMessage(message);
      setHasError(true);
      setErrorMessage(message);
      return;
    }

    let returnUrl = window.location.href;
    try {
      const url = new URL(returnUrl);
      url.searchParams.set('openDatasource', 'true');
      url.searchParams.set('connector', String(type || ''));
      returnUrl = url.toString();
    } catch (error) {
      // Keep current URL as fallback.
    }

    setPluginOAuthAuthorizing(true);
    setPluginOAuthStatus('pending');
    setPluginOAuthMessage(
      t('generic_connector.plugin_oauth.pending') || 'Complete the authentication flow in the opened tab.'
    );
    setHasError(false);
    setErrorMessage('');
    startPluginOAuthAuthorizeTimeout();

    try {
      ws.send(JSON.stringify({
        command: pluginOAuthConfig.commands.authorize,
        caseid,
        datasourceId: selectedDatasourceId,
        type,
        account: pluginOAuthAccountValue,
        provider: pluginOAuthConfig.provider,
        returnUrl,
        ...(pluginOAuthRuntimeIsGeneric && Object.keys(oauthConfigPayload).length > 0
          ? { oauthConfig: oauthConfigPayload }
          : {}),
      }));
    } catch (error) {
      clearPluginOAuthAuthorizeTimeout();
      setPluginOAuthAuthorizing(false);
      setPluginOAuthStatus('disconnected');
      const message = t('generic_connector.plugin_oauth.request_failed') || 'Failed to start OAuth authorization.';
      setPluginOAuthMessage(message);
      setHasError(true);
      setErrorMessage(message);
    }
  };

  const handlePluginConnectorOAuthDisconnect = () => {
    if (!pluginOAuthConfig) {
      return;
    }
    const oauthConfigPayload = buildPluginOAuthConfigPayload();
    setPluginOAuthStatus('disconnected');
    setPluginOAuthVerified(false);
    setPluginOAuthMessage(t('generic_connector.plugin_oauth.disconnect_pending') || 'OAuth connection will be removed when you save.');
    setFormValues((prev) => ({
      ...prev,
      [pluginOAuthConfig.authKey]: {
        ...(prev?.[pluginOAuthConfig.authKey] && typeof prev[pluginOAuthConfig.authKey] === 'object'
          ? prev[pluginOAuthConfig.authKey]
          : {}),
        account: pluginOAuthAccountValue,
        verified: false,
        disconnect: true,
        ...(pluginOAuthRuntimeIsGeneric && Object.keys(oauthConfigPayload).length > 0
          ? { oauthConfig: oauthConfigPayload }
          : {}),
      },
    }));
    setIsSaveDisabled(false);
  };

  const handleOutlookApiAuthorize = () => {
    if (type !== "outlookapi") return;
    if (!selectedDatasourceId || selectedDatasourceId === "new") {
      setHasError(true);
      setErrorMessage(saveBeforeSetupMessage);
      return;
    }
    const userEmail = formValues.account || datasourceName || "";
    if (!userEmail) {
      setHasError(true);
      setErrorMessage(t('generic_connector.outlook_missing_account') || "Enter an account email first.");
      return;
    }
    if (ws) {
      setLoading(true);
      ws.send(JSON.stringify({
        command: "authorizeOutlookApi",
        userEmail,
        caseid,
        returnUrl: window.location.href
      }));
    }
  }


  const handleGmailApiAuthorize = () => {
    if (type !== "Gmail") return;
    if (!selectedDatasourceId || selectedDatasourceId === "new") {
      setHasError(true);
      setErrorMessage(saveBeforeSetupMessage);
      return;
    }
    const userEmail = formValues.account || datasourceName || "";
    if (!userEmail) {
      setHasError(true);
      setErrorMessage(t('generic_connector.gmail_missing_account') || "Enter an account email first.");
      return;
    }
    if (ws) {
      setLoading(true);
      ws.send(JSON.stringify({
        command: "authorizeGmailApi",
        userEmail,
        caseid,
        returnUrl: window.location.href
      }));
    }
  }

  const handleOneDriveApiAuthorize = () => {
    if (type !== "OneDrive") return;
    if (!selectedDatasourceId || selectedDatasourceId === "new") {
      setHasError(true);
      setErrorMessage(saveBeforeSetupMessage);
      return;
    }
    const userEmail = formValues.account || datasourceName || "";
    if (!userEmail) {
      setHasError(true);
      setErrorMessage(t('generic_connector.onedrive_missing_account') || "Enter an account email first.");
      return;
    }
    if (ws) {
      setLoading(true);
      try {
        // Keep an opener-side handle so we can navigate this window when OAuth URL arrives.
        const w = window.open('about:blank', '_blank');
        if (w) {
          w.document.write('<html><body><p>Authorizing OneDrive...</p></body></html>');
          authWindowRef.current = w;
        }
      } catch (err) {
        console.warn('Popup blocked or failed to open', err);
      }
      ws.send(JSON.stringify({
        command: "authorizeOneDriveApi",
        userEmail,
        caseid,
        returnUrl: window.location.href
      }));
    }
  }

  const handleGoogleDriveApiAuthorize = () => {
    if (type !== "GoogleDrive") return;
    if (!selectedDatasourceId || selectedDatasourceId === "new") {
      setHasError(true);
      setErrorMessage(saveBeforeSetupMessage);
      return;
    }
    const userEmail = formValues.account || datasourceName || "";
    if (!userEmail) {
      setHasError(true);
      setErrorMessage(t('generic_connector.googledrive_missing_account') || "Enter an account email first.");
      return;
    }
    if (ws) {
      setLoading(true);
      try {
        // Keep an opener-side handle so we can navigate this window when OAuth URL arrives.
        const w = window.open('about:blank', '_blank');
        if (w) {
          w.document.write('<html><body><p>Authorizing Google Drive...</p></body></html>');
          authWindowRef.current = w;
        }
      } catch (err) {
        console.warn('Popup blocked or failed to open', err);
      }
      ws.send(JSON.stringify({
        command: "authorizeGoogleDriveApi",
        userEmail,
        caseid,
        returnUrl: window.location.href
      }));
    }
  }

  const connectionProvider = useMemo(() => {
    if (pluginOAuthConfig) {
      return {
        isPluginOAuth: true,
        providerLabel: pluginOAuthConfig.label,
        accountRequired: pluginOAuthConfig.accountRequired,
        connectLabel: pluginOAuthConfig.connectLabel || t('generic_connector.plugin_oauth.connect', {
          provider: pluginOAuthConfig.label,
          defaultValue: `Connect ${pluginOAuthConfig.label}`,
        }),
        disconnectLabel: pluginOAuthConfig.disconnectLabel || t('generic_connector.plugin_oauth.disconnect', 'Disconnect'),
        connectHandler: handlePluginConnectorOAuthAuthorize,
        disconnectHandler: handlePluginConnectorOAuthDisconnect,
        statusConnectedKey: 'generic_connector.connection_status_connected',
        statusDisconnectedKey: 'generic_connector.connection_status_disconnected',
      };
    }

    const providers = {
      gmail: {
        isPluginOAuth: false,
        accountLabelKey: 'generic_connector.gmail_account_label',
        accountPlaceholderKey: 'generic_connector.gmail_account_placeholder',
        accountHelperKey: 'generic_connector.gmail_account_helper',
        connectLabelKey: 'generic_connector.connect_gmail_api',
        reconnectLabelKey: 'generic_connector.reconnect_gmail_api',
        connectHandler: handleGmailApiAuthorize,
        statusConnectedKey: 'generic_connector.connection_status_connected',
        statusDisconnectedKey: 'generic_connector.connection_status_disconnected'
      },
      outlookapi: {
        isPluginOAuth: false,
        accountLabelKey: 'generic_connector.outlook_account_label',
        accountPlaceholderKey: 'generic_connector.outlook_account_placeholder',
        accountHelperKey: 'generic_connector.outlook_account_helper',
        connectLabelKey: 'generic_connector.connect_outlook_api',
        connectHandler: handleOutlookApiAuthorize,
        statusConnectedKey: 'generic_connector.connection_status_connected',
        statusDisconnectedKey: 'generic_connector.connection_status_disconnected'
      },
      onedrive: {
        isPluginOAuth: false,
        accountLabelKey: 'generic_connector.onedrive_account_label',
        accountPlaceholderKey: 'generic_connector.onedrive_account_placeholder',
        accountHelperKey: 'generic_connector.onedrive_account_helper',
        connectLabelKey: 'generic_connector.connect_onedrive_api',
        connectHandler: handleOneDriveApiAuthorize,
        statusConnectedKey: 'generic_connector.connection_status_connected',
        statusDisconnectedKey: 'generic_connector.connection_status_disconnected'
      },
      googledrive: {
        isPluginOAuth: false,
        accountLabelKey: 'generic_connector.googledrive_account_label',
        accountPlaceholderKey: 'generic_connector.googledrive_account_placeholder',
        accountHelperKey: 'generic_connector.googledrive_account_helper',
        connectLabelKey: 'generic_connector.connect_googledrive_api',
        connectHandler: handleGoogleDriveApiAuthorize,
        statusConnectedKey: 'generic_connector.connection_status_connected',
        statusDisconnectedKey: 'generic_connector.connection_status_disconnected'
      }
    };
    return providers[String(type || '').toLowerCase()] || null;
  }, [
    type,
    t,
    pluginOAuthConfig,
    handlePluginConnectorOAuthAuthorize,
    handlePluginConnectorOAuthDisconnect,
    handleGmailApiAuthorize,
    handleOutlookApiAuthorize,
    handleOneDriveApiAuthorize,
    handleGoogleDriveApiAuthorize
  ]);

  const requiresConnection = Boolean(connectionProvider);
  const isDatasourcePersisted = Boolean(selectedDatasourceId && selectedDatasourceId !== "new");
  const lockAdvancedSetupUntilSaved = !isDatasourcePersisted;
  const saveBeforeSetupMessage = t('generic_connector.save_before_setup') || "Save this datasource first. You can save it enabled or disabled.";
  const connectionStatus = connectionProvider?.isPluginOAuth
    ? pluginOAuthStatus
    : (connectorConnected ? 'connected' : 'disconnected');
  const connectionIsConnected = connectionStatus === 'connected';
  const showConnectionAccountInput = Boolean(
    connectionProvider && (!connectionProvider.isPluginOAuth || connectionProvider.accountRequired)
  );
  const isNewDatasourceWizard = isCreationWizardActive;
  const getNextCreationWizardStep = useCallback((currentStep) => {
    if (currentStep === 0) {
      return requiresConnection ? 1 : 2;
    }
    if (currentStep === 1) {
      return 2;
    }
    if (currentStep === 2) {
      return 3;
    }
    return 3;
  }, [requiresConnection]);
  const getPreviousCreationWizardStep = useCallback((currentStep) => {
    if (currentStep === 3) {
      return 2;
    }
    if (currentStep === 2) {
      return requiresConnection ? 1 : 0;
    }
    if (currentStep === 1) {
      return 0;
    }
    return 0;
  }, [requiresConnection]);
  const creationWizardSteps = useMemo(() => ([
    {
      label: t('generic_connector.wizard.general_title') || 'Datasource',
      caption: t('generic_connector.wizard.general_caption') || 'Create and save the datasource'
    },
    {
      label: t('generic_connector.wizard.connection_title') || 'Connection',
      caption: requiresConnection
        ? (t('generic_connector.wizard.connection_caption') || 'Authenticate this datasource')
        : (t('generic_connector.wizard.connection_optional') || 'No authentication required')
    },
    {
      label: t('generic_connector.wizard.settings_title') || 'Settings',
      caption: t('generic_connector.wizard.settings_caption') || 'Complete connector settings'
    },
    {
      label: t('generic_connector.wizard.ingestion_title') || 'Start',
      caption: t('generic_connector.wizard.ingestion_caption') || 'Turn it on now or leave it off'
    }
  ]), [requiresConnection, t]);
  const showGeneralSettingsSection = !showPluginGuide && !isNewDatasourceWizard;
  const showConnectionSection = !showPluginGuide && !isNewDatasourceWizard && requiresConnection;
  const showConnectorSettingsSection = !showPluginGuide && !isNewDatasourceWizard && isDatasourcePersisted && connectorUI;
  const activeDatasourceCacheKey = getDatasourceCacheKey(selectedDatasourceId);
  const hasLoadedActiveDatasource = Boolean(
    activeDatasourceCacheKey &&
    lastLoadedDatasourceKeyRef.current === activeDatasourceCacheKey
  );
  const shouldShowMainLoadingState = Boolean(
    open &&
    selectedDatasourceId &&
    !isNewDatasourceWizard &&
    loading &&
    !showPluginGuide &&
    !hasLoadedActiveDatasource
  );
  const shouldHideMainDialogForCreationBootstrap = Boolean(
    open &&
    !isStandaloneCreationWizard &&
    loading &&
    (!selectedDatasourceId || selectedDatasourceId === "new") &&
    datasourceList.length === 0
  );

  const handleForceRun = useCallback((datasourceIdOverride = null) => {
    const isEventLike = Boolean(
      datasourceIdOverride &&
      typeof datasourceIdOverride === 'object' &&
      (
        typeof datasourceIdOverride.preventDefault === 'function' ||
        typeof datasourceIdOverride.stopPropagation === 'function'
      )
    );
    if (isEventLike) {
      datasourceIdOverride.stopPropagation?.();
    }
    const targetDatasourceId = isEventLike ? selectedDatasourceId : (datasourceIdOverride ?? selectedDatasourceId);
    if (!ws || !targetDatasourceId || targetDatasourceId === "new") return;

    clearForceRunTimeout();
    setForceRunStatus('info');
    setForceRunMessage(t('generic_connector.starting') || "Starting...");
    setHasError(false);
    setErrorMessage("");
    setForceRunLoading(true);
    startForceRunTimeout();
    ws.send(JSON.stringify({
      command: "forceRunDatasource",
      caseid,
      datasource_id: targetDatasourceId
    }));
  }, [ws, selectedDatasourceId, clearForceRunTimeout, t, startForceRunTimeout, caseid]);

  const isCurrentDsScanning = selectedDatasourceId && scanningDatasourceIds.has(selectedDatasourceId);

  const handleOpenIngestions = () => {
    if (!selectedDatasourceId || selectedDatasourceId === "new") {
      return;
    }
    setIngestionsOpen(true);
    requestDatasourceIngestions();
  };

  const handleCloseIngestions = () => {
    setIngestionsOpen(false);
  };

  const hasDatasourceProfile = Boolean(hasDatasourceProfileHint);
  const connectorBadgeActive = hasAnyEnabled || connectorEnabled || hasDatasourceEnabledHint;
  const connectionBadgeActive = (
    type === "Gmail"
    || type === "outlookapi"
    || type === "OneDrive"
    || type === "GoogleDrive"
    || Boolean(pluginOAuthConfig)
  ) && hasAnyConnected;
  const formattedLastExecution = formatLastUpdateLabel(lastExecution);
  const hasLastExecutionValue = !!formattedLastExecution;
  const connectorPluginQuickStartSteps = useMemo(() => ([
    t('generic_connector.plugin_page.quick_start_step_1') || "Create serverv2/Connectors/ConnectorMySource.py with class ConnectorMySource(ConnectorParent).",
    t('generic_connector.plugin_page.quick_start_step_2') || "Define self.title and self.components in __init__ to describe the settings form.",
    t('generic_connector.plugin_page.quick_start_step_3') || "Implement verify_connection() and Ingest(datasource, case, force=False, requesting_user_id=None).",
    t('generic_connector.plugin_page.quick_start_step_4') || "Add the connector card in app/v2/src/components/SettingDatasources.js.",
    t('generic_connector.plugin_page.quick_start_step_5') || "Restart backend and refresh the datasource page."
  ]), [t]);
  const connectorPluginRuntimeNotes = useMemo(() => ([
    t('generic_connector.plugin_page.runtime_note_1') || "Connector UI is loaded dynamically with command getGenericSourceConnectorData.",
    t('generic_connector.plugin_page.runtime_note_2') || "Datasource list and saves use listGenericDatasources and saveGenericDatasource.",
    t('generic_connector.plugin_page.runtime_note_3') || "Sensitive values are redacted/masked by backend before returning saved settings.",
    t('generic_connector.plugin_page.runtime_note_4') || "Unlike action plugins, connector discovery is code-driven; new types require frontend registration."
  ]), [t]);
  const connectorDisplayName = String(title || type || t('generic_connector.title')).trim();
  const connectorDescKey = type ? `generic_connector.connectors.${type}_description` : null;
  const connectorDescription = (connectorDescKey && t(connectorDescKey) !== connectorDescKey) ? t(connectorDescKey) : t('generic_connector.datasources_hint');
  const hasPluginMemory = Boolean(effectivePluginMemory);
  const creationWizardTitle = t('generic_connector.wizard.dialog_title', {
    connector: connectorDisplayName,
    defaultValue: `${connectorDisplayName} setup`
  });
  const creationWizardPrimaryLabel = useMemo(() => {
    if (!isNewDatasourceWizard) {
      return t('generic_connector.wizard.next_action') || 'Next';
    }
    if (creationWizardStep === 2) {
      return t('generic_connector.wizard.save_continue_action') || 'Save and Continue';
    }
    if (creationWizardStep === 3) {
      return creationWizardIngestionMode === 'now'
        ? (t('generic_connector.wizard.start_action') || 'Start it')
        : (t('generic_connector.wizard.skip_action') || 'Not now');
    }
    return t('generic_connector.wizard.next_action') || 'Next';
  }, [creationWizardIngestionMode, creationWizardStep, isNewDatasourceWizard, t]);
  const creationWizardPrimaryIcon = useMemo(() => {
    if (!isNewDatasourceWizard) {
      return undefined;
    }
    if (creationWizardStep === 2) {
      return <SaveIcon />;
    }
    if (creationWizardStep === 3 && creationWizardIngestionMode === 'now') {
      return <PlayArrowIcon />;
    }
    return null;
  }, [creationWizardIngestionMode, creationWizardStep, isNewDatasourceWizard]);
  const creationWizardPrimaryDisabled = creationWizardStep === 1
    ? (loading || !connectionIsConnected || pluginOAuthAuthorizing || pluginOAuthChecking)
    : (creationWizardStep === 3
      ? (loading || forceRunLoading || !ws || (creationWizardIngestionMode === 'now' && (!selectedDatasourceId || selectedDatasourceId === "new")))
      : (loading || !ws));
  const connectorTooltipContent = (
    <Box sx={{ p: 1, maxWidth: 400 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{title || t('generic_connector.title')}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{connectorDescription}</Typography>
    </Box>
  );
  const iconTooltip = isPluginUnavailable
    ? (hasError ? (errorMessage || t('generic_connector.connector_error')) : loadFailureMessage)
    : connectorTooltipContent;
  const wizardStepCardSx = {
    p: 2.5,
    borderRadius: 3,
    background: `linear-gradient(180deg, rgba(250,250,255,0.98), rgba(245,246,250,0.95))`,
    border: '1px solid rgba(87,87,176,0.08)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6), 0 6px 20px rgba(24,24,48,0.04)'
  };
  const wizardStepIconWrapSx = {
    width: 42,
    height: 42,
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: 'linear-gradient(135deg, rgba(87,87,176,0.14), rgba(87,87,176,0.05))',
    border: '1px solid rgba(87,87,176,0.12)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)'
  };
  const renderCreationWizardHeader = (iconElement, titleText, subtitleText) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={wizardStepIconWrapSx}>
        {iconElement}
      </Box>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: themeColors.text, lineHeight: 1.2 }}>
          {titleText}
        </Typography>
        {subtitleText && (
          <Typography variant="caption" color="text.secondary">
            {subtitleText}
          </Typography>
        )}
      </Box>
    </Box>
  );
  const getWizardChoiceCardSx = (isSelected) => ({
    p: 2,
    borderRadius: 2,
    textAlign: 'left',
    border: isSelected ? `2px solid ${themeColors.primary}` : '1px solid rgba(24,24,24,0.08)',
    background: isSelected
      ? 'linear-gradient(135deg, rgba(87,87,176,0.08), rgba(87,87,176,0.02))'
      : 'rgba(255,255,255,0.92)',
    boxShadow: isSelected
      ? '0 8px 20px rgba(87,87,176,0.12)'
      : '0 3px 12px rgba(0,0,0,0.04)'
  });



  const getConnectorIcon = () => {
    if (icon) return icon;

    if (useImageIcon) {
      return (
        <Box
          component="img"
          src={imageSrc}
          alt={`${type} icon`}
          sx={{
            width: iconSize,
            height: iconSize,
            objectFit: 'contain',
            opacity: iconError ? 0.5 : 1
          }}
          onError={() => {
            const primaryImageSrc = getDefaultConnectorImage(type);
            const lowercaseFallbackSrc = getLowercaseConnectorImage(type);
            if (imageSrc === primaryImageSrc && lowercaseFallbackSrc !== primaryImageSrc) {
              setImageSrc(lowercaseFallbackSrc);
              return;
            }
            if (imageSrc !== icon_default) {
              setImageSrc(icon_default);
              return;
            }
            console.warn(`Could not load icon for connector type: ${type}`);
            setIconError(true);
          }}
        />
      );
    }

    return ConnectorIcons[type] ? React.cloneElement(ConnectorIcons[type], { sx: { fontSize: iconSize } }) : <SettingsIcon sx={{ fontSize: iconSize }} />;
  };
  const renderCreationWizardContent = () => {
    if (creationWizardStep === 0) {
      return (
        <Paper elevation={0} sx={wizardStepCardSx}>
          {renderCreationWizardHeader(
            <TuneIcon sx={{ color: themeColors.primary, fontSize: 22 }} />,
            t('generic_connector.wizard.basics_title') || "Let's start with the basics",
            t('generic_connector.wizard.basics_hint') || "Give this connector a name."
          )}
          <StyledPaper condensed withBlur sx={{ p: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <StyledTextField
                label={t('generic_connector.wizard.name_label') || "Give this connector a name"}
                name="datasourceName"
                value={datasourceName}
                sx={{ mb: 0, flex: 1 }}
                onChange={(e) => {
                  setDatasourceName(e.target.value);
                  setIsSaveDisabled(false);
                }}
                placeholder={t('generic_connector.wizard.name_placeholder') || "e.g., Weekly product updates"}
                helperText={t('generic_connector.wizard.name_helper') || "Choose a name you'll recognize later."}
              />
              <InfoButton textKey={'generic_connector.datasource_name_info'} ariaLabel="Datasource name info" />
            </Box>
          </StyledPaper>
        </Paper>
      );
    }

    if (creationWizardStep === 1) {
      if (!requiresConnection) {
        return (
          <Paper elevation={0} sx={wizardStepCardSx}>
            {renderCreationWizardHeader(
              <SettingsIcon sx={{ color: themeColors.primary, fontSize: 22 }} />,
              t('generic_connector.connection_section_title') || "Connection",
              t('generic_connector.wizard.connection_optional') || 'No authentication required'
            )}
            <StyledPaper condensed withBlur sx={{ p: 1.25, mb: 0 }}>
              <Typography variant="body2" color="text.secondary">
                {t('generic_connector.wizard.connection_optional') || 'No authentication required'}
              </Typography>
            </StyledPaper>
          </Paper>
        );
      }

      return (
        <Paper elevation={0} sx={wizardStepCardSx}>
          {renderCreationWizardHeader(
            <SettingsIcon sx={{ color: themeColors.primary, fontSize: 22 }} />,
            t('generic_connector.connection_section_title') || "Connection",
            t('generic_connector.connection_section_hint') || "Authenticate and verify access for this connector"
          )}

          <StyledPaper condensed withBlur sx={{ mb: 0, p: 1.25 }}>
            <Box sx={{
              display: 'flex',
              gap: 1,
              alignItems: { xs: 'stretch', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap'
            }}>
              {connectionProvider && showConnectionAccountInput && (
                <StyledTextField
                  label={connectionProvider.isPluginOAuth
                    ? (t('generic_connector.plugin_oauth.account_label', {
                      provider: connectionProvider.providerLabel,
                      defaultValue: `${connectionProvider.providerLabel} account`,
                    }))
                    : (t(connectionProvider.accountLabelKey) || "Account (email)")}
                  name={connectionProvider.isPluginOAuth ? "pluginOAuthAccount" : "account"}
                  value={connectionProvider.isPluginOAuth ? pluginOAuthAccount : (formValues.account || '')}
                  sx={{ mb: 0, minWidth: 220, flex: 1 }}
                  disabled={loading || lockAdvancedSetupUntilSaved}
                  onChange={(e) => {
                    if (connectionProvider.isPluginOAuth) {
                      setPluginOAuthAccount(e.target.value);
                    } else {
                      handleFieldChange('account', e.target.value);
                    }
                  }}
                  placeholder={connectionProvider.isPluginOAuth
                    ? (t('generic_connector.plugin_oauth.account_placeholder', {
                      defaultValue: 'user@company.com',
                    }))
                    : (t(connectionProvider.accountPlaceholderKey) || "user@company.com")}
                  helperText={connectionProvider.isPluginOAuth
                    ? (t('generic_connector.plugin_oauth.account_helper', {
                      defaultValue: 'Required to connect.',
                    }))
                    : (t(connectionProvider.accountHelperKey) || "Required to connect")}
                />
              )}

              {connectionProvider?.isPluginOAuth && pluginOAuthRuntimeIsGeneric && (
                <Box sx={{ width: '100%', display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                  <StyledTextField
                    label={t('generic_connector.plugin_oauth.authorize_url_label', 'Authorize URL')}
                    value={pluginOAuthOverrides.authorizeUrl}
                    onChange={(event) => handlePluginOAuthOverrideChange('authorizeUrl', event.target.value)}
                    placeholder="https://provider.example.com/oauth/authorize"
                    helperText={t('generic_connector.plugin_oauth.authorize_url_helper', 'Required for generic OAuth providers.')}
                    disabled={loading || lockAdvancedSetupUntilSaved}
                    sx={{ mb: 0 }}
                  />
                  <StyledTextField
                    label={t('generic_connector.plugin_oauth.token_url_label', 'Token URL')}
                    value={pluginOAuthOverrides.tokenUrl}
                    onChange={(event) => handlePluginOAuthOverrideChange('tokenUrl', event.target.value)}
                    placeholder="https://provider.example.com/oauth/token"
                    helperText={t('generic_connector.plugin_oauth.token_url_helper', 'Required for generic OAuth providers.')}
                    disabled={loading || lockAdvancedSetupUntilSaved}
                    sx={{ mb: 0 }}
                  />
                  <StyledTextField
                    label={t('generic_connector.plugin_oauth.client_id_label', 'Client ID')}
                    value={pluginOAuthOverrides.clientId}
                    onChange={(event) => handlePluginOAuthOverrideChange('clientId', event.target.value)}
                    helperText={t('generic_connector.plugin_oauth.client_id_helper', 'Required unless Client ID Env is provided.')}
                    disabled={loading || lockAdvancedSetupUntilSaved}
                    sx={{ mb: 0 }}
                  />
                  <StyledTextField
                    label={t('generic_connector.plugin_oauth.client_id_env_label', 'Client ID Env')}
                    value={pluginOAuthOverrides.clientIdEnv}
                    onChange={(event) => handlePluginOAuthOverrideChange('clientIdEnv', event.target.value)}
                    helperText={t('generic_connector.plugin_oauth.client_id_env_helper', 'Optional environment variable name for client ID.')}
                    disabled={loading || lockAdvancedSetupUntilSaved}
                    sx={{ mb: 0 }}
                  />
                  <StyledTextField
                    label={t('generic_connector.plugin_oauth.client_secret_label', 'Client Secret')}
                    value={pluginOAuthOverrides.clientSecret}
                    onChange={(event) => handlePluginOAuthOverrideChange('clientSecret', event.target.value)}
                    type="password"
                    helperText={t('generic_connector.plugin_oauth.client_secret_helper', 'Optional if the provider supports public clients.')}
                    disabled={loading || lockAdvancedSetupUntilSaved}
                    sx={{ mb: 0 }}
                  />
                  <StyledTextField
                    label={t('generic_connector.plugin_oauth.client_secret_env_label', 'Client Secret Env')}
                    value={pluginOAuthOverrides.clientSecretEnv}
                    onChange={(event) => handlePluginOAuthOverrideChange('clientSecretEnv', event.target.value)}
                    helperText={t('generic_connector.plugin_oauth.client_secret_env_helper', 'Optional environment variable name for client secret.')}
                    disabled={loading || lockAdvancedSetupUntilSaved}
                    sx={{ mb: 0 }}
                  />
                </Box>
              )}

              {connectionProvider && (
                <Button
                  variant="outlined"
                  onClick={connectionProvider.connectHandler}
                  disabled={
                    loading
                    || lockAdvancedSetupUntilSaved
                    || pluginOAuthAuthorizing
                    || pluginOAuthChecking
                    || (connectionProvider.isPluginOAuth && connectionStatus === 'connected')
                  }
                >
                  {connectionProvider.isPluginOAuth
                    ? (connectionProvider.connectLabel || `Connect ${connectionProvider.providerLabel}`)
                    : (connectorConnected && connectionProvider.reconnectLabelKey
                      ? (t(connectionProvider.reconnectLabelKey) || "Reconnect")
                      : (t(connectionProvider.connectLabelKey) || "Connect"))}
                </Button>
              )}

              {connectionProvider?.isPluginOAuth && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={connectionProvider.disconnectHandler}
                  disabled={lockAdvancedSetupUntilSaved || connectionStatus === 'disconnected'}
                >
                  {connectionProvider.disconnectLabel || (t('generic_connector.plugin_oauth.disconnect') || 'Disconnect')}
                </Button>
              )}

              {connectionProvider && (
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    color: connectionIsConnected ? '#1b5e20' : connectionStatus === 'pending' ? '#8a6d00' : '#616161',
                    backgroundColor: connectionIsConnected
                      ? 'rgba(76,175,80,0.12)'
                      : connectionStatus === 'pending'
                        ? 'rgba(255,193,7,0.12)'
                        : 'rgba(120,120,120,0.1)',
                    border: connectionIsConnected
                      ? '1px solid rgba(76,175,80,0.3)'
                      : connectionStatus === 'pending'
                        ? '1px solid rgba(255,193,7,0.35)'
                        : '1px solid rgba(120,120,120,0.2)'
                  }}
                >
                  {connectionStatus === 'pending'
                    ? (t('generic_connector.connection_status_pending') || "Pending")
                    : connectionIsConnected
                      ? (t(connectionProvider.statusConnectedKey) || "Connected")
                      : (t(connectionProvider.statusDisconnectedKey) || "Not connected")}
                </Box>
              )}

              {connectionProvider?.isPluginOAuth && pluginOAuthMessage && (
                <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                  {pluginOAuthMessage}
                </Typography>
              )}
              {connectionProvider?.isPluginOAuth && pluginOAuthConfig?.helperText && (
                <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                  {pluginOAuthConfig.helperText}
                </Typography>
              )}
            </Box>
          </StyledPaper>
        </Paper>
      );
    }

    if (creationWizardStep === 2) {
      return (
        <Paper elevation={0} sx={wizardStepCardSx}>
          {renderCreationWizardHeader(
            <SettingsIcon sx={{ color: themeColors.primary, fontSize: 22 }} />,
            t(`generic_connector.connection_settings`) || "Connector Settings",
            t(`generic_connector.connection_settings_hint`) || "Configure how to connect to your data source"
          )}
          {lockAdvancedSetupUntilSaved && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {saveBeforeSetupMessage}
            </Typography>
          )}
          <StyledPaper sx={{ p: 0, boxShadow: 'none', borderRadius: 2, background: 'transparent', border: 'none', mb: 0 }}>
            {connectorUI?.components && Array.isArray(connectorUI.components) ? (
              connectorUI.components.length > 0 ? (
                connectorUI.components.map((component, index) => (
                  <DynamicFormControl
                    key={index}
                    component={component}
                    value={formValues[component?.name]}
                    onChange={handleFieldChange}
                    disabled={lockAdvancedSetupUntilSaved}
                    folderPickerContext={folderPickerContext}
                  />
                ))
              ) : (
                <Typography color="textSecondary" align="center">
                  {t('generic_connector.no_additional_settings') || "No additional settings required"}
                </Typography>
              )
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                {loading ? <CircularProgress size={24} /> : (
                  <Typography color="error" align="center">
                    {t('generic_connector.invalid_config') || "Invalid configuration received"}
                  </Typography>
                )}
              </Box>
            )}
            {supportsSinceDate && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  type="datetime-local"
                  label={t('generic_connector.received_since') || "Received since"}
                  value={formatDateTimeLocalInput(lastUpdateInput || lastUpdate)}
                  onChange={(event) => {
                    const rawValue = String(event.target.value || "").trim();
                    if (!rawValue) {
                      handleSinceDateChange(null);
                      return;
                    }
                    const parsed = dayjs(rawValue);
                    handleSinceDateChange(parsed.isValid() ? parsed : null);
                  }}
                  disabled={lockAdvancedSetupUntilSaved}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    minWidth: 260,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: themeColors.primary,
                      },
                    },
                    '& label.Mui-focused': {
                      color: themeColors.primary,
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {t('generic_connector.received_since_help') || "Default is 6 months ago. Adjust to limit how far back to ingest."}
                </Typography>
              </Box>
            )}
          </StyledPaper>
        </Paper>
      );
    }

    return (
      <Paper elevation={0} sx={wizardStepCardSx}>
        {renderCreationWizardHeader(
          <PlayArrowIcon sx={{ color: themeColors.primary, fontSize: 22 }} />,
          t('generic_connector.wizard.ingestion_prompt_title') || "Start it?",
          t('generic_connector.wizard.ingestion_prompt_hint') || "Turn this connector on now or leave it off."
        )}
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          <Paper
            component={ButtonBase}
            onClick={() => setCreationWizardIngestionMode('later')}
            sx={getWizardChoiceCardSx(creationWizardIngestionMode === 'later')}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: themeColors.text, mb: 0.5 }}>
              {t('generic_connector.wizard.ingestion_later_title') || 'Not now'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('generic_connector.wizard.ingestion_later_caption') || 'Leave it off'}
            </Typography>
          </Paper>
          <Paper
            component={ButtonBase}
            onClick={() => setCreationWizardIngestionMode('now')}
            sx={getWizardChoiceCardSx(creationWizardIngestionMode === 'now')}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: themeColors.text, mb: 0.5 }}>
              {t('generic_connector.wizard.ingestion_now_title') || 'Start it'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('generic_connector.wizard.ingestion_now_caption') || 'Turn it on'}
            </Typography>
          </Paper>
        </Box>
      </Paper>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        p={1}
        width={{ xs: '100%', sm: 130, md: 150 }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
          }
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center">
          <Tooltip
            title={iconTooltip}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'rgba(255,255,255,0.98)',
                  color: 'rgba(0,0,0,0.87)',
                  border: '1px solid rgba(0,0,0,0.08)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                  '& .MuiTooltip-arrow': { color: 'rgba(255,255,255,0.98)' },
                }
              }
            }}
          >
            <div
              className="icon-container"
              style={{
                position: 'relative',
                cursor: isPluginUnavailable ? 'not-allowed' : 'pointer',
                background: 'rgba(245, 245, 245, 0.8)',
                borderRadius: '50%',
                padding: containerPadding,
                border: hasDatasourceProfile ? '2px solid rgba(87, 87, 176, 0.8)' : '2px solid transparent',
                boxShadow: connectorBadgeActive
                  ? '0 0 15px rgba(87, 87, 176, 0.6)'
                  : hasDatasourceProfile
                    ? '0 0 10px rgba(87, 87, 176, 0.25)'
                    : 'none',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{ width: iconSize, height: iconSize, display: 'flex', alignItems: 'center', justifyContent: 'center', filter: isPluginUnavailable ? 'grayscale(100%) opacity(50%)' : (connectorBadgeActive || hasDatasourceProfile) ? 'none' : 'grayscale(100%)', transition: 'all 0.3s ease', transform: isPluginUnavailable ? 'scale(0.95)' : connectorBadgeActive ? 'scale(1.05)' : hasDatasourceProfile ? 'scale(1.02)' : 'scale(1)', cursor: isPluginUnavailable ? 'not-allowed' : 'pointer' }} onClick={isPluginUnavailable ? undefined : handleOpen}>
                {getConnectorIcon()}
              </div>

              {aiGenerated && (
                <Tooltip
                  title={hasPluginMemory
                    ? t('generic_connector.memory_dialog_open', 'Show generation memory')
                    : t('generic_connector.memory_dialog_empty', 'No memory information is available for this plugin.')}
                  arrow
                >
                  <Box
                    component="button"
                    type="button"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setIsPluginMemoryDialogOpen(true);
                    }}
                    sx={{
                      position: 'absolute',
                      top: -7,
                      right: -7,
                      width: isMobile ? 20 : 24,
                      height: isMobile ? 20 : 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      color: '#ffffff',
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                      border: '2px solid #ffffff',
                      boxShadow: '0 5px 14px rgba(37, 99, 235, 0.4)',
                      zIndex: 3,
                      p: 0,
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: isMobile ? 11 : 14 }} />
                  </Box>
                </Tooltip>
              )}

              {(connectorBadgeActive || connectionBadgeActive) && (
                <Tooltip
                  title={
                    connectionBadgeActive && connectorBadgeActive
                      ? `${t('generic_connector.connection_status_connected') || "Connected"} • ${t('generic_connector.status_enabled') || "Enabled"}`
                      : connectionBadgeActive
                        ? (t('generic_connector.connection_status_connected') || "Connected")
                        : (t('generic_connector.status_enabled') || "Enabled")
                  }
                  arrow
                >
                  <Box
                    onClick={isPluginUnavailable ? undefined : handleOpen}
                    sx={{
                      position: 'absolute',
                      top: connectorBadgeActive ? -7 : -4,
                      left: connectorBadgeActive ? -7 : -4,
                      borderRadius: '50%',
                      width: connectorBadgeActive ? (isMobile ? 20 : 24) : (isMobile ? 14 : 16),
                      height: connectorBadgeActive ? (isMobile ? 20 : 24) : (isMobile ? 14 : 16),
                      border: connectorBadgeActive
                        ? '2px solid #ffffff'
                        : (connectionBadgeActive ? '2px solid #4caf50' : '2px solid transparent'),
                      background: connectorBadgeActive
                        ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                        : '#ffffff',
                      boxShadow: connectorBadgeActive
                        ? '0 5px 14px rgba(34, 197, 94, 0.38)'
                        : '0 2px 5px rgba(0,0,0,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffffff',
                      zIndex: 3,
                      p: 0,
                      cursor: isPluginUnavailable ? 'default' : 'pointer',
                      pointerEvents: 'auto',
                    }}
                  >
                    {connectorBadgeActive ? (
                      <PlayArrowIcon sx={{ fontSize: isMobile ? 11 : 14, ml: '1px' }} />
                    ) : (
                      <Box
                        sx={{
                          width: isMobile ? 6 : 7,
                          height: isMobile ? 6 : 7,
                          borderRadius: '50%',
                          backgroundColor: '#4caf50'
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>
              )}

              {loading && (
                <CircularProgress size={progressSize} style={{ position: 'absolute', top: progressOffset, left: progressOffset, color: themeColors.primary }} />
              )}
              {hasError && (
                <Tooltip title={errorMessage || t(`generic_connector.connector_error`) || "Connector not available"} arrow>
                  <div style={{ position: 'absolute', top: '-5px', right: '-5px', backgroundColor: '#f44336', borderRadius: '50%', width: isMobile ? '18px' : '22px', height: isMobile ? '18px' : '22px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', boxShadow: '0 2px 5px rgba(0,0,0,0.2)', animation: 'pulse 1.5s infinite' }}>
                    <ErrorIcon style={{ fontSize: isMobile ? '13px' : '16px' }} />
                  </div>
                </Tooltip>
              )}
            </div>
          </Tooltip>

          <Popover
            open={Boolean(anchorInfoContent) && Boolean(anchorInfoPos)}
            anchorReference="anchorPosition"
            anchorPosition={anchorInfoPos || undefined}
            onClose={handleInfoClose}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{ sx: { p: 1, maxWidth: 320, zIndex: (theme) => theme.zIndex.modal + 10 } }}
          >
            {anchorInfoContent ? (
              <Box sx={{ p: 1, maxWidth: 320 }}>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{anchorInfoContent}</Typography>
              </Box>
            ) : null}
          </Popover>
          <Typography variant="caption" sx={{ mt: 1, fontSize: captionFontSize, textAlign: 'center', color: (connectorBadgeActive || hasDatasourceProfile) ? themeColors.primary : 'text.secondary', fontWeight: (connectorBadgeActive || hasDatasourceProfile) ? 'medium' : 'normal', transition: 'all 0.3s ease' }}>
            {title || t(`generic_connector.caption`)}
          </Typography>
        </Box>

        <GenericConnectorIngestions
          open={ingestionsOpen}
          onClose={handleCloseIngestions}
          ingestionFilter={ingestionFilter}
          setIngestionFilter={setIngestionFilter}
          ingestionsLoading={ingestionsLoading}
          ingestionsError={ingestionsError}
          ingestionItems={ingestionItems}
          filteredIngestionItems={filteredIngestionItems}
          getIngestionSource={getIngestionSource}
          getIngestionTitle={getIngestionTitle}
          formatIngestionTimestamp={formatIngestionTimestamp}
          getIngestionStatus={getIngestionStatus}
          getIngestionStatusLabel={getIngestionStatusLabel}
          getIngestionStatusColor={getIngestionStatusColor}
        />

        <Dialog
          open={Boolean(open && !isNewDatasourceWizard && !isStandaloneCreationWizard && !shouldHideMainDialogForCreationBootstrap)}
          onClose={handleClose}
          maxWidth="md"
          fullWidth
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 400 }}
          PaperProps={{
            elevation: 8,
            sx: {
              borderRadius: { xs: 0, md: 4 },
              background: `linear-gradient(to bottom right, ${themeColors.cardBackground}, ${themeColors.background})`,
              minHeight: { xs: '100vh', md: '80vh' },
              maxHeight: { xs: '100vh', md: '90vh' },
              width: { xs: '100%', sm: 'calc(100% - 64px)' },
              m: { xs: 0, sm: 4 },
              px: { xs: 1, md: 0 }
            }
          }}
        >
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryDark} 100%)`,
              color: themeColors.lightText,
              px: 4,
              py: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '12px', padding: '10px', display: 'flex' }}>
                {icon || (ConnectorIcons[type] ? React.createElement(ConnectorIcons[type], { sx: { fontSize: 32 } }) : <SettingsIcon sx={{ fontSize: 32 }} />)}
              </div>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                  {title || t(`generic_connector.title`)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500 }}>
                  {t('generic_connector.instances_count', { count: datasourceList.length })}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              {hasConnectorGuide && (
                <Tooltip title={connectorGuideTooltipLabel} arrow>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenConnectorGuide}
                    sx={{
                      color: themeColors.lightText,
                      borderColor: 'rgba(255,255,255,0.45)',
                      fontWeight: 700,
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.8)',
                        backgroundColor: 'rgba(255,255,255,0.08)'
                      }
                    }}
                  >
                    {connectorGuideButtonLabel}
                  </Button>
                </Tooltip>
              )}
              <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 0.5, minWidth: 0, flexShrink: 0 }}>
                <Tooltip title={connectorEnabled ? (t('generic_connector.status_enabled') || "Enabled") : (t('generic_connector.status_disabled') || "Disabled")} arrow>
                  <IconButton
                    size="medium"
                    onClick={() => handleConnectorToggle(!connectorEnabled)}
                    aria-label={connectorEnabled ? 'Disable datasource' : 'Enable datasource'}
                    sx={{
                      color: connectorEnabled ? toggleColors.on : toggleColors.off,
                      p: 0.75,
                      transition: 'transform 0.12s ease',
                      '&:hover': { transform: 'scale(1.05)' }
                    }}
                  >
                    {connectorEnabled ? <ToggleOnIcon sx={{ fontSize: toggleIconSizes.mobile }} /> : <ToggleOffIcon sx={{ fontSize: toggleIconSizes.mobile }} />}
                  </IconButton>
                </Tooltip>
                <InfoButton textKey={`generic_connector.enable_connector_tooltip`} ariaLabel="Enable connector info" sx={{ color: themeColors.lightText }} />
                {selectedDatasourceId && selectedDatasourceId !== "new" && (
                  <Tooltip title={isCurrentDsScanning ? (t('generic_connector.scanning') || "Scanning...") : (forceRunLoading ? (t('generic_connector.starting') || "Starting...") : (t('generic_connector.force_run') || "Force Run"))} arrow>
                    <span>
                      <IconButton size="small" onClick={handleForceRun} disabled={loading || forceRunLoading || isCurrentDsScanning} sx={{ color: themeColors.lightText, p: 0.5 }}>
                        {forceRunLoading || isCurrentDsScanning ? <CircularProgress size={18} /> : <PlayArrowIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
              <CloseButton ariaLabel="close" onClick={handleClose} sx={{ color: themeColors.lightText }} />
            </Box>
          </DialogTitle>

          <DialogContent sx={{ "--dialog-content-top-padding": "0px", p: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: { xs: 2, sm: 4 }, flex: 1, overflowY: 'auto' }}>
              {selectedDatasourceId && selectedDatasourceId !== "new" && forceRunMessage && (
                <Box sx={{ mb: 2 }}>
                  <Chip
                    size="small"
                    color={forceRunStatus === 'success' ? 'success' : (forceRunStatus === 'error' ? 'error' : 'info')}
                    variant={forceRunStatus === 'info' ? 'outlined' : 'filled'}
                    label={forceRunMessage}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              )}
              <Box sx={{ mb: 4 }}>
                <Typography variant="overline" sx={{ color: themeColors.primary, fontWeight: 800, letterSpacing: '0.1em' }}>
                  {t(`generic_connector.your_datasources`) || "Your Datasources"}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(220px, 1fr))' },
                    gap: { xs: 2, sm: 3 },
                    mt: 2
                  }}
                >
                  {datasourceList.map((ds) => {
                    const isActive = selectedDatasourceId === ds.id;
                    const isEnabled = ds.enable === 1;
                    const isToggling = togglingDatasourceIds.has(ds.id);
                    const relativeLabel = formatRelativeTimestamp(ds.lastExecution || ds.last_update || ds.lastUpdate);

                    return (
                      <Tooltip
                        title={t(`generic_connector.configure_existing_hint`) || "Modify settings for the selected datasource"}
                        placement="top"
                        arrow
                        key={ds.id}
                      >
                        <Paper
                          onClick={() => {
                            handleSelectDatasource(ds.id);
                            setDatasourceName(ds.name || '');
                          }}
                          sx={{
                            textAlign: 'left',
                            p: 2,
                            borderRadius: 2,
                            border: isActive ? `2px solid ${themeColors.primary}` : '1px solid rgba(24,24,24,0.06)',
                            background: isActive ? `linear-gradient(135deg, rgba(87,87,176,0.12), rgba(87,87,176,0.03))` : 'rgba(255, 255, 255, 1)',
                            boxShadow: isActive ? '0 8px 20px rgba(87, 87, 176, 0.14)' : '0 2px 6px rgba(0,0,0,0.04)',
                            transition: 'transform 0.15s, box-shadow 0.15s, border 0.15s',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 120,
                            alignItems: 'flex-start',
                            gap: 0.75,
                            position: 'relative',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: isActive ? '0 10px 26px rgba(87, 87, 176, 0.18)' : '0 6px 14px rgba(0,0,0,0.08)',
                              border: isActive ? `2px solid ${themeColors.primaryLight}` : `1px solid rgba(87,87,176,0.08)`,
                            },
                            '&:hover .datasource-actions': {
                              opacity: 1,
                              pointerEvents: 'auto',
                              transform: 'translateY(0)'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0, pr: 5 }}>
                              {editingNameId === ds.id ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                                  <TextField
                                    value={editingNameValue}
                                    onChange={(e) => setEditingNameValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleSaveEditName(e, ds);
                                      }
                                      if (e.key === 'Escape') {
                                        handleCancelEditName(e);
                                      }
                                    }}
                                    size="small"
                                    fullWidth
                                    sx={{ '& .MuiInputBase-input': { fontWeight: 700 } }}
                                  />
                                  <IconButton size="small" onClick={(e) => handleSaveEditName(e, ds)} disabled={savingNameId === ds.id} aria-label="Save name">
                                    {savingNameId === ds.id ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
                                  </IconButton>
                                  <IconButton size="small" onClick={(e) => handleCancelEditName(e)} aria-label="Cancel edit">
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: themeColors.text, mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ds.name || `${title || type} #${ds.id}`}>
                                    {ds.name || `${title || type} #${ds.id}`}
                                  </Typography>
                                  <IconButton size="small" onClick={(e) => handleStartEditName(e, ds)} aria-label="Edit name" sx={{ color: 'text.secondary' }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  {savedNameSuccessId === ds.id && (
                                    <Box sx={{ ml: 0.5, color: 'success.main', fontWeight: 700 }}>{t('generic_connector.saved') || 'Saved'}</Box>
                                  )}
                                </Box>
                              )}
                              {/* ID moved into tooltip for clarity */}
                            </Box>
                          </Box>
                          {/* Summary removed here — moved into footer for consistent placement */}
                          <Box
                            className="datasource-actions"
                            onClick={(event) => {
                              event.stopPropagation();
                              const confirmDelete = window.confirm(t('generic_connector.confirm_delete') || "Delete this datasource?");
                              if (!confirmDelete) {
                                return;
                              }
                              setLoading(true);
                              ws.send(JSON.stringify({
                                command: "deleteGenericDatasource",
                                caseid,
                                type,
                                datasource_id: ds.id
                              }));
                            }}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              opacity: 0,
                              pointerEvents: 'none',
                              transform: 'translateY(-6px)',
                              transition: 'opacity 0.18s ease, transform 0.18s ease',
                            }}
                          >
                            <CloseButton size="small" color="error" sx={{ bgcolor: 'rgba(255,255,255,0.92)', boxShadow: '0 2px 6px rgba(0,0,0,0.08)', borderRadius: 1 }} />
                          </Box>

                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mt: 'auto', gap: 0.5 }}>
                            <Tooltip title={isEnabled ? t('generic_connector.status_enabled') || "Enabled" : t('generic_connector.status_disabled') || "Disabled"} arrow>
                              <IconButton
                                onClick={(e) => handleQuickToggleEnable(e, ds)}
                                sx={{ color: isEnabled ? toggleColors.on : toggleColors.off, p: 0.75, transition: 'transform 0.12s ease' }}
                                aria-label={isToggling ? 'Updating datasource' : (isEnabled ? 'Disable datasource' : 'Enable datasource')}
                                disabled={isToggling}
                                size="medium"
                              >
                                {isToggling ? (
                                  <CircularProgress size={20} thickness={5} sx={{ color: themeColors.primary }} />
                                ) : (isEnabled ? <ToggleOnIcon sx={{ fontSize: toggleIconSizes.list }} /> : <ToggleOffIcon sx={{ fontSize: toggleIconSizes.list }} />)}
                              </IconButton>
                            </Tooltip>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: isEnabled ? '#4caf50' : '#bdbdbd', boxShadow: isEnabled ? '0 0 8px rgba(76,175,80,0.2)' : 'none' }} />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{relativeLabel}</Typography>
                              {scanningDatasourceIds.has(ds.id) && (
                                <Tooltip title={t('generic_connector.scanning') || "Scanning..."} arrow>
                                  <CircularProgress size={14} thickness={6} sx={{ color: themeColors.primary, ml: 0.5 }} />
                                </Tooltip>
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('generic_connector.interval_short', { value: formatTimeInterval(ds.interval || 300), defaultValue: `Every ${formatTimeInterval(ds.interval || 300)}` })}</Typography>
                          </Box>
                        </Paper>
                      </Tooltip>
                    );
                  })}
                  <Paper
                    component={ButtonBase}
                    onClick={() => handleSelectDatasource("new")}
                    sx={{
                      p: { xs: 1.5, sm: 1.75 },
                      borderRadius: 2,
                      border: selectedDatasourceId === "new" ? `2px dashed ${themeColors.primary}` : '1px dashed rgba(87,87,176,0.12)',
                      background: selectedDatasourceId === "new" ? `linear-gradient(135deg, rgba(87,87,176,0.06), rgba(87,87,176,0.02))` : 'rgba(255, 255, 255, 0.98)',
                      color: themeColors.primary,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      transition: 'all 0.15s ease',
                      minHeight: { xs: 140, sm: 120 },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 14px rgba(0,0,0,0.08)',
                        border: `2px dashed ${themeColors.primaryLight}`,
                        background: 'rgba(87, 87, 176, 0.04)',
                      }
                    }}
                  >
                    <AddCircleOutlineIcon sx={{ fontSize: 30, color: themeColors.primary }} />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      {t(`generic_connector.add_new_datasource`) || "Create New"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('generic_connector.add_new_hint') || "Configure a new datasource"}
                    </Typography>
                  </Paper>
                </Box>
              </Box >

              {/* Configuration Section - Only visible when a datasource is selected */}
              {
                selectedDatasourceId && !isNewDatasourceWizard && (
                  <Fade in={!!selectedDatasourceId} timeout={400}>
                    <Box sx={{
                      background: 'linear-gradient(180deg, rgba(250,250,255,0.98), rgba(245,247,251,0.95))',
                      border: '1px solid rgba(0,0,0,0.04)',
                      borderRadius: 2,
                      p: { xs: 2, md: 3 },
                      mb: 2,
                      borderLeft: `4px solid ${themeColors.primaryLight}`
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        gap: { xs: 1.5, sm: 2 },
                        mb: 3,
                        pb: 2,
                        borderBottom: `2px solid ${themeColors.primary}`
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: '1 1 auto' }}>
                          <SettingsIcon sx={{ color: themeColors.primary, fontSize: 28 }} />
                          <Box sx={{ minWidth: 0 }}>
                            {selectedDatasourceId === "new" ? (
                              <>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: themeColors.text }}>
                                  {t(`generic_connector.configure_new`) || "Configure New Datasource"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {t(`generic_connector.configure_new_hint`) || "Set up a new datasource connection"}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: themeColors.primary, letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 520 }} title={datasourceName || `${title || type} #${selectedDatasourceId}`}>
                                  {datasourceName || `${title || type} #${selectedDatasourceId}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                                  {`${title || type} • #${selectedDatasourceId}`}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                          {hasConnectorGuide && (
                            <Tooltip title={connectorGuideTooltipLabel} arrow>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={handleOpenConnectorGuide}
                                sx={{
                                  minWidth: 0,
                                  px: 1.25,
                                  py: 0.35,
                                  borderColor: 'rgba(87,87,176,0.35)',
                                  color: themeColors.primary,
                                  fontWeight: 700,
                                  lineHeight: 1
                                }}
                              >
                                {connectorGuideButtonLabel}
                              </Button>
                            </Tooltip>
                          )}
                          <Tooltip title={connectorEnabled ? (t('generic_connector.status_enabled') || "Enabled") : (t('generic_connector.status_disabled') || "Disabled")} arrow>
                            <IconButton
                              onClick={() => handleConnectorToggle(!connectorEnabled)}
                              aria-label={connectorEnabled ? 'Disable datasource' : 'Enable datasource'}
                              sx={{
                                color: connectorEnabled ? toggleColors.on : toggleColors.off,
                                p: 0.75,
                                transition: 'transform 0.12s ease',
                                '&:hover': { transform: 'scale(1.05)' }
                              }}
                            >
                              {connectorEnabled ? <ToggleOnIcon sx={{ fontSize: { xs: toggleIconSizes.mobile, sm: toggleIconSizes.desktop } }} /> : <ToggleOffIcon sx={{ fontSize: { xs: toggleIconSizes.mobile, sm: toggleIconSizes.desktop } }} />}
                            </IconButton>
                          </Tooltip>
                          <InfoButton textKey={`generic_connector.enable_connector_tooltip`} ariaLabel="Enable connector info" />
                          {selectedDatasourceId && selectedDatasourceId !== "new" && (
                            <Tooltip title={isCurrentDsScanning ? (t('generic_connector.scanning') || "Scanning...") : (forceRunLoading ? (t('generic_connector.starting') || "Starting...") : (t('generic_connector.force_run') || "Force Run"))} arrow>
                              <span>
                                <IconButton size="small" onClick={handleForceRun} disabled={loading || forceRunLoading || isCurrentDsScanning} sx={{ color: themeColors.primary }}>
                                  {forceRunLoading || isCurrentDsScanning ? <CircularProgress size={18} /> : <PlayArrowIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>

                      {shouldShowMainLoadingState ? (
                        <Paper
                          elevation={0}
                          sx={{
                            p: { xs: 3, md: 4 },
                            mb: 0,
                            borderRadius: 3,
                            minHeight: 280,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            background: 'linear-gradient(180deg, rgba(250,250,255,0.98), rgba(245,247,251,0.95))',
                            border: '1px solid rgba(87,87,176,0.08)',
                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65), 0 8px 24px rgba(24,24,48,0.05)'
                          }}
                        >
                          <Box sx={{ maxWidth: 360 }}>
                            <CircularProgress size={38} sx={{ color: themeColors.primary, mb: 2 }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: themeColors.text }}>
                              {t('generic_connector.loading_connector_title') || 'Loading connector configuration...'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {t('generic_connector.loading_connector_hint') || 'Retrieving datasource status, connection details, and connector settings.'}
                            </Typography>
                          </Box>
                        </Paper>
                      ) : showPluginGuide ? (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            mb: 0,
                            borderRadius: 3,
                            background: `linear-gradient(180deg, rgba(255,251,246,0.98), rgba(255,247,240,0.95))`,
                            backgroundImage: `radial-gradient(circle at 88% 8%, rgba(255,138,101,0.06), transparent 18%), repeating-linear-gradient(90deg, rgba(255,255,255,0.02) 0 8px, transparent 8px 16px)`,
                            border: '1px solid rgba(255,138,101,0.14)',
                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 6px 20px rgba(255,138,101,0.08)',
                            borderLeft: '4px solid rgba(255,138,101,0.28)'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 2 }}>
                            <InfoIcon sx={{ color: themeColors.accent, fontSize: 22, mt: 0.2 }} />
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 800, color: themeColors.text, lineHeight: 1.2 }}>
                                {t('generic_connector.plugin_page.title') || "Connector Plugin Guide"}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {t('generic_connector.plugin_page.subtitle') || "Inspired by action plugins: build class-based connector plugins with dynamic form fields."}
                              </Typography>
                            </Box>
                          </Box>

                          <StyledPaper condensed withBlur sx={{ mb: 1.25 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, color: themeColors.text }}>
                              {t('generic_connector.plugin_page.what_is_title') || "1) What a connector plugin is"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('generic_connector.plugin_page.what_is_body') || "A connector plugin is a Python class in serverv2/Connectors that exposes UI fields through self.components and ingests data through Ingest."}
                            </Typography>
                          </StyledPaper>

                          <StyledPaper condensed withBlur sx={{ mb: 1.25 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, color: themeColors.text }}>
                              {t('generic_connector.plugin_page.where_title') || "2) Where connectors live"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {t('generic_connector.plugin_page.where_body') || "Create Connector<YourType>.py in serverv2/Connectors and expose class Connector<YourType>. Add the type in SettingDatasources to show it in this page."}
                            </Typography>
                            <Box component="code" sx={{ display: 'inline-block', px: 1, py: 0.5, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.04)', color: themeColors.text, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              serverv2/Connectors/ConnectorMySource.py
                            </Box>
                          </StyledPaper>

                          <StyledPaper condensed withBlur sx={{ mb: 1.25 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, color: themeColors.text }}>
                              {t('generic_connector.plugin_page.quick_start_title') || "3) Quick start"}
                            </Typography>
                            <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
                              {connectorPluginQuickStartSteps.map((stepText, index) => (
                                <Box component="li" key={`connector-plugin-step-${index}`} sx={{ mb: 0.75 }}>
                                  <Typography variant="body2" color="text.secondary">{stepText}</Typography>
                                </Box>
                              ))}
                            </Box>
                          </StyledPaper>

                          <StyledPaper condensed withBlur sx={{ mb: 1.25 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, color: themeColors.text }}>
                              {t('generic_connector.plugin_page.field_types_title') || "4) Supported UI field types"}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              {CONNECTOR_PLUGIN_FIELD_TYPES.map((fieldType) => (
                                <Box
                                  key={fieldType}
                                  sx={{
                                    px: 1.1,
                                    py: 0.45,
                                    borderRadius: 999,
                                    border: '1px solid rgba(87,87,176,0.2)',
                                    bgcolor: 'rgba(87,87,176,0.08)',
                                    color: themeColors.primary,
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                    fontWeight: 700
                                  }}
                                >
                                  {fieldType}
                                </Box>
                              ))}
                            </Box>
                          </StyledPaper>

                          <StyledPaper condensed withBlur sx={{ mb: 1.25 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, color: themeColors.text }}>
                              {t('generic_connector.plugin_page.class_template_title') || "5) Class + UI template"}
                            </Typography>
                            <Box component="pre" sx={{ m: 0, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(24,24,40,0.94)', color: '#f4f7ff', overflowX: 'auto', fontSize: '0.75rem', lineHeight: 1.45, fontFamily: 'monospace' }}>
                              {CONNECTOR_PLUGIN_CLASS_TEMPLATE}
                            </Box>
                          </StyledPaper>

                          <StyledPaper condensed withBlur sx={{ mb: 1.25 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, color: themeColors.text }}>
                              {t('generic_connector.plugin_page.ingest_template_title') || "6) Ingest contract template"}
                            </Typography>
                            <Box component="pre" sx={{ m: 0, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(24,24,40,0.94)', color: '#f4f7ff', overflowX: 'auto', fontSize: '0.75rem', lineHeight: 1.45, fontFamily: 'monospace' }}>
                              {CONNECTOR_PLUGIN_INGEST_TEMPLATE}
                            </Box>
                          </StyledPaper>

                          <StyledPaper condensed withBlur sx={{ mb: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, color: themeColors.text }}>
                              {t('generic_connector.plugin_page.runtime_title') || "7) Runtime notes"}
                            </Typography>
                            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                              {connectorPluginRuntimeNotes.map((note, index) => (
                                <Box component="li" key={`connector-plugin-note-${index}`} sx={{ mb: 0.75 }}>
                                  <Typography variant="body2" color="text.secondary">{note}</Typography>
                                </Box>
                              ))}
                            </Box>
                          </StyledPaper>
                        </Paper>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {showConnectionSection && (
                      <Box sx={{ order: 2 }}>
                      {/* Connection Section */}
                        <Paper elevation={0} sx={{
                          p: 2,
                          mb: 2,
                          background: `linear-gradient(90deg, rgba(245,255,248,0.98), rgba(238,251,244,0.95))`,
                          backgroundImage: `radial-gradient(circle at 90% 10%, rgba(76,175,80,0.05), transparent 18%), repeating-linear-gradient(90deg, rgba(255,255,255,0.02) 0 8px, transparent 8px 16px)`,
                          border: '1px solid rgba(76,175,80,0.12)',
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6), 0 6px 20px rgba(76,175,80,0.06)',
                          borderLeft: '4px solid rgba(76,175,80,0.35)',
                          borderRadius: 3
                        }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 1,
                            pb: 0.5
                          }}>
                            <SettingsIcon sx={{ color: '#2e7d32', fontSize: 22 }} />
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: themeColors.text, lineHeight: 1.2 }}>
                                {t('generic_connector.connection_section_title') || "Connection"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('generic_connector.connection_section_hint') || "Authenticate and verify access for this connector"}
                              </Typography>
                            </Box>
                          </Box>

                          <StyledPaper condensed withBlur sx={{ mb: 0, p: 1 }}>
                            <Box sx={{
                              display: 'flex',
                              gap: 1,
                              alignItems: { xs: 'stretch', sm: 'center' },
                              flexDirection: { xs: 'column', sm: 'row' },
                              flexWrap: 'wrap'
                            }}>
                              {connectionProvider && showConnectionAccountInput && (
                                <StyledTextField
                                  label={connectionProvider.isPluginOAuth
                                    ? (t('generic_connector.plugin_oauth.account_label', {
                                      provider: connectionProvider.providerLabel,
                                      defaultValue: `${connectionProvider.providerLabel} account`,
                                    }))
                                    : (t(connectionProvider.accountLabelKey) || "Account (email)")}
                                  name={connectionProvider.isPluginOAuth ? "pluginOAuthAccount" : "account"}
                                  value={connectionProvider.isPluginOAuth ? pluginOAuthAccount : (formValues.account || '')}
                                  sx={{ mb: 0, minWidth: 220 }}
                                  disabled={loading || lockAdvancedSetupUntilSaved}
                                  onChange={(e) => {
                                    if (connectionProvider.isPluginOAuth) {
                                      setPluginOAuthAccount(e.target.value);
                                    } else {
                                      handleFieldChange('account', e.target.value);
                                    }
                                  }}
                                  placeholder={connectionProvider.isPluginOAuth
                                    ? (t('generic_connector.plugin_oauth.account_placeholder', {
                                      defaultValue: 'user@company.com',
                                    }))
                                    : (t(connectionProvider.accountPlaceholderKey) || "user@company.com")}
                                  helperText={connectionProvider.isPluginOAuth
                                    ? (t('generic_connector.plugin_oauth.account_helper', {
                                      defaultValue: 'Required to connect.',
                                    }))
                                    : (t(connectionProvider.accountHelperKey) || "Required to connect")}
                                />
                              )}

                              {connectionProvider?.isPluginOAuth && pluginOAuthRuntimeIsGeneric && (
                                <Box sx={{ width: '100%', display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                  <StyledTextField
                                    label={t('generic_connector.plugin_oauth.authorize_url_label', 'Authorize URL')}
                                    value={pluginOAuthOverrides.authorizeUrl}
                                    onChange={(event) => handlePluginOAuthOverrideChange('authorizeUrl', event.target.value)}
                                    placeholder="https://provider.example.com/oauth/authorize"
                                    helperText={t('generic_connector.plugin_oauth.authorize_url_helper', 'Required for generic OAuth providers.')}
                                    disabled={loading || lockAdvancedSetupUntilSaved}
                                    sx={{ mb: 0 }}
                                  />
                                  <StyledTextField
                                    label={t('generic_connector.plugin_oauth.token_url_label', 'Token URL')}
                                    value={pluginOAuthOverrides.tokenUrl}
                                    onChange={(event) => handlePluginOAuthOverrideChange('tokenUrl', event.target.value)}
                                    placeholder="https://provider.example.com/oauth/token"
                                    helperText={t('generic_connector.plugin_oauth.token_url_helper', 'Required for generic OAuth providers.')}
                                    disabled={loading || lockAdvancedSetupUntilSaved}
                                    sx={{ mb: 0 }}
                                  />
                                  <StyledTextField
                                    label={t('generic_connector.plugin_oauth.client_id_label', 'Client ID')}
                                    value={pluginOAuthOverrides.clientId}
                                    onChange={(event) => handlePluginOAuthOverrideChange('clientId', event.target.value)}
                                    helperText={t('generic_connector.plugin_oauth.client_id_helper', 'Required unless Client ID Env is provided.')}
                                    disabled={loading || lockAdvancedSetupUntilSaved}
                                    sx={{ mb: 0 }}
                                  />
                                  <StyledTextField
                                    label={t('generic_connector.plugin_oauth.client_id_env_label', 'Client ID Env')}
                                    value={pluginOAuthOverrides.clientIdEnv}
                                    onChange={(event) => handlePluginOAuthOverrideChange('clientIdEnv', event.target.value)}
                                    helperText={t('generic_connector.plugin_oauth.client_id_env_helper', 'Optional environment variable name for client ID.')}
                                    disabled={loading || lockAdvancedSetupUntilSaved}
                                    sx={{ mb: 0 }}
                                  />
                                  <StyledTextField
                                    label={t('generic_connector.plugin_oauth.client_secret_label', 'Client Secret')}
                                    value={pluginOAuthOverrides.clientSecret}
                                    onChange={(event) => handlePluginOAuthOverrideChange('clientSecret', event.target.value)}
                                    type="password"
                                    helperText={t('generic_connector.plugin_oauth.client_secret_helper', 'Optional if the provider supports public clients.')}
                                    disabled={loading || lockAdvancedSetupUntilSaved}
                                    sx={{ mb: 0 }}
                                  />
                                  <StyledTextField
                                    label={t('generic_connector.plugin_oauth.client_secret_env_label', 'Client Secret Env')}
                                    value={pluginOAuthOverrides.clientSecretEnv}
                                    onChange={(event) => handlePluginOAuthOverrideChange('clientSecretEnv', event.target.value)}
                                    helperText={t('generic_connector.plugin_oauth.client_secret_env_helper', 'Optional environment variable name for client secret.')}
                                    disabled={loading || lockAdvancedSetupUntilSaved}
                                    sx={{ mb: 0 }}
                                  />
                                </Box>
                              )}

                              {connectionProvider && (
                                <Button
                                  variant="outlined"
                                  onClick={connectionProvider.connectHandler}
                                  disabled={
                                    loading
                                    || lockAdvancedSetupUntilSaved
                                    || pluginOAuthAuthorizing
                                    || pluginOAuthChecking
                                    || (connectionProvider.isPluginOAuth && connectionStatus === 'connected')
                                  }
                                >
                                  {connectionProvider.isPluginOAuth
                                    ? (connectionProvider.connectLabel || `Connect ${connectionProvider.providerLabel}`)
                                    : (connectorConnected && connectionProvider.reconnectLabelKey
                                      ? (t(connectionProvider.reconnectLabelKey) || "Reconnect")
                                      : (t(connectionProvider.connectLabelKey) || "Connect"))}
                                </Button>
                              )}

                              {connectionProvider?.isPluginOAuth && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  onClick={connectionProvider.disconnectHandler}
                                  disabled={lockAdvancedSetupUntilSaved || connectionStatus === 'disconnected'}
                                >
                                  {connectionProvider.disconnectLabel || (t('generic_connector.plugin_oauth.disconnect') || 'Disconnect')}
                                </Button>
                              )}

                              {connectionProvider && (
                                <Box
                                  sx={{
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    letterSpacing: '0.02em',
                                    color: connectionIsConnected ? '#1b5e20' : connectionStatus === 'pending' ? '#8a6d00' : '#616161',
                                    backgroundColor: connectionIsConnected
                                      ? 'rgba(76,175,80,0.12)'
                                      : connectionStatus === 'pending'
                                        ? 'rgba(255,193,7,0.12)'
                                        : 'rgba(120,120,120,0.1)',
                                    border: connectionIsConnected
                                      ? '1px solid rgba(76,175,80,0.3)'
                                      : connectionStatus === 'pending'
                                        ? '1px solid rgba(255,193,7,0.35)'
                                        : '1px solid rgba(120,120,120,0.2)'
                                  }}
                                >
                                  {connectionStatus === 'pending'
                                    ? (t('generic_connector.connection_status_pending') || "Pending")
                                    : connectionIsConnected
                                      ? (t(connectionProvider.statusConnectedKey) || "Connected")
                                      : (t(connectionProvider.statusDisconnectedKey) || "Not connected")}
                                </Box>
                              )}

                              {connectionProvider?.isPluginOAuth && pluginOAuthMessage && (
                                <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                                  {pluginOAuthMessage}
                                </Typography>
                              )}
                              {connectionProvider?.isPluginOAuth && pluginOAuthConfig?.helperText && (
                                <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                                  {pluginOAuthConfig.helperText}
                                </Typography>
                              )}
                              {lockAdvancedSetupUntilSaved && (
                                <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                                  {saveBeforeSetupMessage}
                                </Typography>
                              )}
                            </Box>
                          </StyledPaper>
                        </Paper>
                      {/* End Connection Section */}
                      </Box>
                      )}

                      {/* General Settings Section */}
                      {showGeneralSettingsSection && (
                      <Box sx={{ order: 1 }}>
                      <Paper elevation={0} sx={{
                        p: 2,
                        mb: 0,
                        background: `linear-gradient(180deg, rgba(250,250,255,0.98), rgba(245,245,250,0.95))`,
                        backgroundImage: `radial-gradient(circle at 10% 10%, rgba(87,87,176,0.02), transparent 15%), repeating-linear-gradient(135deg, rgba(255,255,255,0.02) 0 6px, transparent 6px 12px)`,
                        border: '1px solid rgba(87,87,176,0.04)',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6), 0 6px 20px rgba(24,24,48,0.04)',
                        borderLeft: '4px solid rgba(87,87,176,0.08)'
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          mb: 1,
                          pb: 0.5
                        }}>
                          <TuneIcon sx={{ color: themeColors.primary, fontSize: 20 }} />
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: themeColors.text, lineHeight: 1.2 }}>
                              {t(`generic_connector.general_settings`) || "General Settings"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t(`generic_connector.general_settings_hint`) || "Basic configuration for this datasource"}
                            </Typography>
                          </Box>
                        </Box>

                      <StyledPaper condensed withBlur sx={{ mb: 0.75, p: 1 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          alignItems: { xs: 'stretch', sm: 'center' },
                          flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                          <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <StyledTextField
                                label={t(`generic_connector.datasource_name`) || "Datasource Name"}
                                name="datasourceName"
                                value={datasourceName}
                                sx={{ mb: 0, flex: 1, minWidth: 0 }}
                                onChange={(e) => {
                                  setDatasourceName(e.target.value);
                                  setIsSaveDisabled(false);
                                }}
                                placeholder={t(`generic_connector.datasource_name_placeholder`) || "e.g., News Feed A, CSV Import 1"}
                                helperText={t('generic_connector.name_helper') || "Give this datasource a memorable name"}
                              />

                              <InfoButton textKey={'generic_connector.datasource_name_info'} ariaLabel="Datasource name info" />

                            </Box>
                          </Box>
                          <Box sx={{ width: { xs: '100%', sm: 'auto' } }} />
                        </Box>
                      </StyledPaper>



                      <StyledPaper condensed withBlur sx={{ mb: 0.75, p: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                          <StyledTextField
                            label={t(`generic_connector.interval_label`) || "Check Interval (seconds)"}
                            type="number"
                            value={interval}
                            onChange={(e) => setInterval(e.target.value)}
                            InputProps={{
                              inputProps: { min: 300 }
                            }}
                            sx={{ mb: 0, flex: 1, minWidth: 0 }}
                            helperText={`${t(`generic_connector.interval_help`) || "Minimum 5 minutes"} (${formatTimeInterval(interval)})`}
                          />
                          <InfoButton textKey={`generic_connector.interval_tooltip`} ariaLabel="Interval info" />
                        </Box>
                      </StyledPaper>

                      <StyledPaper
                        condensed
                        withBlur
                        sx={{ p: 0 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.75, flexWrap: 'wrap' }}>
                          <Box sx={{ p: 2, minHeight: 96, borderRadius: 1.5, background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: hasLastExecutionValue ? 'success.main' : 'grey.400', transform: hasLastExecutionValue ? 'scale(1.05)' : 'none' }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontFamily: 'inherit' }}>
                                    {t('generic_connector.last_execution', { defaultValue: 'Last execution' })}
                                  </Typography>
                                  <InfoButton textKey={`generic_connector.last_execution_tooltip`} ariaLabel="Last execution info" sx={{ color: 'text.secondary', ml: 0.5 }} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto' }}>
                                  {selectedDatasourceId && selectedDatasourceId !== "new" && (
                                    <Tooltip title={normalizedLastFoundCount > 0 ? lastFoundLabel : (t('generic_connector.new_events_zero') || 'No new events')} arrow>
                                      <Box role="button" tabIndex={0} sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }} onClick={(e) => handleOpenInfo(e, normalizedLastFoundCount > 0 ? lastFoundLabel : (t('generic_connector.new_events_zero') || 'No new events'))}>
                                        <Badge
                                          badgeContent={normalizedLastFoundCount}
                                          overlap="circular"
                                          showZero
                                          sx={{
                                            '& .MuiBadge-badge': {
                                              bgcolor: normalizedLastFoundCount > 0 ? 'success.main' : 'grey.400',
                                              color: 'common.white',
                                              boxShadow: '0 0 0 1px rgba(0,0,0,0.08)'
                                            }
                                          }}
                                        >
                                          <NewspaperIcon aria-label="new events" sx={{ color: normalizedLastFoundCount > 0 ? 'success.main' : 'text.secondary', fontSize: 22 }} />
                                        </Badge>
                                      </Box>
                                    </Tooltip>
                                  )}

                                  {selectedDatasourceId && selectedDatasourceId !== "new" && (
                                    <Tooltip title={normalizedLastSkippedCount > 0 ? lastSkippedLabel : (t('generic_connector.skipped_zero') || 'No skipped items')} arrow>
                                      <Box role="button" tabIndex={0} sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }} onClick={(e) => handleOpenInfo(e, normalizedLastSkippedCount > 0 ? lastSkippedLabel : (t('generic_connector.skipped_zero') || 'No skipped items'))}>
                                        <Badge
                                          badgeContent={normalizedLastSkippedCount}
                                          overlap="circular"
                                          showZero
                                          sx={{
                                            '& .MuiBadge-badge': {
                                              bgcolor: normalizedLastSkippedCount > 0 ? 'warning.main' : 'grey.400',
                                              color: 'common.white',
                                              boxShadow: '0 0 0 1px rgba(0,0,0,0.08)'
                                            }
                                          }}
                                        >
                                          <ReportProblemIcon sx={{ color: normalizedLastSkippedCount > 0 ? 'warning.main' : 'text.secondary', fontSize: 22 }} />
                                        </Badge>
                                      </Box>
                                    </Tooltip>
                                  )}

                                  {selectedDatasourceId && selectedDatasourceId !== "new" && (
                                    <Tooltip title={t('generic_connector.last_ingestions') || "Last ingestions"} arrow>
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={handleOpenIngestions}
                                          disabled={ingestionsLoading}
                                          sx={{ color: themeColors.primary }}
                                        >
                                          {ingestionsLoading ? <CircularProgress size={16} /> : <HistoryIcon fontSize="small" />}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: themeColors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {hasLastExecutionValue ? formattedLastExecution : (t('generic_connector.never_run', 'Never run') || 'Never')}
                                </Typography>
                                {selectedDatasourceId && selectedDatasourceId !== "new" && (!isEditingLastUpdate ? (
                                  <IconButton size="small" sx={{ color: themeColors.primary }} onClick={handleStartEditLastUpdate} aria-label="Edit ingestion start">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                ) : (
                                  <IconButton size="small" sx={{ color: 'text.secondary' }} onClick={handleCancelEditLastUpdate} aria-label="Cancel editing last run">
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                        {isEditingLastUpdate && (
                          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-start' }, gap: 1, mt: 1, flexWrap: 'wrap' }}>
                            <TextField
                              type="datetime-local"
                              label={t('generic_connector.override_last_update') || "Start ingesting from"}
                              value={formatDateTimeLocalInput(lastUpdateInput || lastUpdate)}
                              onChange={(event) => {
                                const rawValue = String(event.target.value || "").trim();
                                if (!rawValue) {
                                  handleLastUpdateChange(null);
                                  return;
                                }
                                const parsed = dayjs(rawValue);
                                handleLastUpdateChange(parsed.isValid() ? parsed : null);
                              }}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                minWidth: 260,
                                '& .MuiOutlinedInput-root': {
                                  '&.Mui-focused fieldset': {
                                    borderColor: themeColors.primary,
                                  },
                                },
                                '& label.Mui-focused': {
                                  color: themeColors.primary,
                                }
                              }}
                            />
                            <Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignItems: 'flex-start' }}>
                              <Button
                                size="small"
                                color="secondary"
                                onClick={handleResetLastUpdate}
                                disabled={loading || lastUpdateSaving}
                              >
                                {t('generic_connector.reset_last_update') || "Mark never run"}
                              </Button>
                              {selectedDatasourceId && selectedDatasourceId !== "new" && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={handleLastUpdateSave}
                                  disabled={loading || lastUpdateSaving}
                                  startIcon={lastUpdateSaving ? <CircularProgress size={16} /> : null}
                                >
                                  {lastUpdateSaved ? (t('generic_connector.saved') || "Saved") : (t('generic_connector.save_last_update') || "Save ingestion start")}
                                </Button>
                              )}
                            </Stack>
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 2 }}>
                          {t('generic_connector.override_last_update_help') || "Use this to change the ingestion start point and re-scan data from that moment."}
                        </Typography>
                      </StyledPaper>
                      </Paper>
                      {/* End General Settings Section */}
                      </Box>
                      )}

                      {/* Connector-Specific Settings Section */}
                      {showConnectorSettingsSection && (
                      <Box sx={{ order: 3 }}>
                      <Divider sx={{ my: 2, borderColor: 'rgba(255,138,101,0.18)', height: 2, backgroundColor: 'rgba(255,138,101,0.06)', borderRadius: 1 }} />

                        <Grow in={Boolean(connectorUI)} timeout={500}>
                          <Paper elevation={0} sx={{
                          p: 2.5,
                          borderRadius: 3,
                          mb: 2,
                          background: `linear-gradient(90deg, rgba(255,250,245,0.98), rgba(255,245,240,0.95))`,
                          backgroundImage: `radial-gradient(circle at 90% 10%, rgba(255,138,101,0.03), transparent 18%), repeating-linear-gradient(90deg, rgba(255,255,255,0.02) 0 8px, transparent 8px 16px)`,
                          border: '1px solid rgba(255,138,101,0.08)',
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6), 0 6px 20px rgba(255,138,101,0.04)',
                          borderLeft: '4px solid rgba(255,138,101,0.08)'
                        }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1.5, 
                              mb: 2,
                              pb: 1
                            }}>
                              <SettingsIcon sx={{ color: themeColors.accent, fontSize: 24 }} />
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: themeColors.text, lineHeight: 1.2 }}>
                                  {t(`generic_connector.connection_settings`) || "Connector Settings"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t(`generic_connector.connection_settings_hint`) || "Configure how to connect to your data source"}
                                </Typography>
                                {lockAdvancedSetupUntilSaved && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {saveBeforeSetupMessage}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <StyledPaper sx={{ p: 0, boxShadow: 'none', borderRadius: 2, background: 'transparent', border: 'none', mb: 0 }}>
                              {connectorUI.components && Array.isArray(connectorUI.components) ? (
                                connectorUI.components.length > 0 ? (
                                  connectorUI.components.map((component, index) => (
                                <DynamicFormControl
                                  key={index}
                                  component={component}
                                  value={formValues[component?.name]}
                                  onChange={handleFieldChange}
                                  disabled={lockAdvancedSetupUntilSaved}
                                  folderPickerContext={folderPickerContext}
                                />
                                  ))
                                ) : (
                                  <Typography color="textSecondary" align="center">
                                    {t('generic_connector.no_additional_settings') || "No additional settings required"}
                                  </Typography>
                                )
                              ) : (
                                <Typography color="error" align="center">
                                  {t('generic_connector.invalid_config') || "Invalid configuration received"}
                                </Typography>
                              )}
                              {supportsSinceDate && (
                                <Box sx={{ mb: 2 }}>
                                  <TextField
                                    type="datetime-local"
                                    label={t('generic_connector.received_since') || "Received since"}
                                    value={formatDateTimeLocalInput(lastUpdateInput || lastUpdate)}
                                    onChange={(event) => {
                                      const rawValue = String(event.target.value || "").trim();
                                      if (!rawValue) {
                                        handleSinceDateChange(null);
                                        return;
                                      }
                                      const parsed = dayjs(rawValue);
                                      handleSinceDateChange(parsed.isValid() ? parsed : null);
                                    }}
                                    disabled={lockAdvancedSetupUntilSaved}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                      minWidth: 260,
                                      '& .MuiOutlinedInput-root': {
                                        '&.Mui-focused fieldset': {
                                          borderColor: themeColors.primary,
                                        },
                                      },
                                      '& label.Mui-focused': {
                                        color: themeColors.primary,
                                      }
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {t('generic_connector.received_since_help') || "Default is 6 months ago. Adjust to limit how far back to ingest."}
                                  </Typography>
                                </Box>
                              )}
                            </StyledPaper>
                          </Paper>
                        </Grow>
                      {/* End Connector-Specific Settings Section */}
                      </Box>
                      )}
                        </Box>
                      )}

                    </Box>
                  </Fade>
                )
              }

              {
                loading && !connectorUI && !selectedDatasourceId && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                  </Box>
                )
              }
            </Box>
          </DialogContent>

          {!isNewDatasourceWizard && (
            <DialogActions sx={{
              backgroundColor: 'rgba(245, 245, 245, 0.5)',
              backdropFilter: 'blur(5px)',
              borderTop: '1px solid rgba(200, 200, 200, 0.3)',
              px: 3,
              py: 2
            }}>
              <ActionButton
                variant="outlined"
                icon={<CloseIcon />}
                label={t('close')}
                onClick={handleClose}
              />
              <ActionButton
                variant="contained"
                icon={<SaveIcon />}
                label={t(`generic_connector.save_button`) || "Save Settings"}
                onClick={handleSaveSettings}
                disabled={isSaveDisabled}
                loading={loading}
                success={saveSuccess ? t(`generic_connector.settings_saved_success`) || "Settings Saved" : null}
                sx={{ ml: 2 }}
              />
            </DialogActions>
          )}
        </Dialog >
        <GenericConnectorWizard
          open={Boolean(open && isNewDatasourceWizard)}
          title={creationWizardTitle}
          steps={creationWizardSteps}
          activeStep={creationWizardStep}
          primaryLabel={creationWizardPrimaryLabel}
          primaryIcon={creationWizardPrimaryIcon}
          primaryDisabled={creationWizardPrimaryDisabled}
          loading={loading}
          showBack={creationWizardStep > 0}
          onBack={handleCreationWizardBack}
          onPrimary={creationWizardStep === 3 ? handleCreationWizardComplete : handleCreationWizardNext}
          onClose={handleCloseCreationWizard}
        >
          {renderCreationWizardContent()}
        </GenericConnectorWizard>
        <Dialog
          open={Boolean(isGuideDialogOpen && hasConnectorGuide)}
          onClose={handleCloseConnectorGuide}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: { xs: 0, md: 2.5 },
              height: { xs: '100vh', md: '88vh' },
              m: { xs: 0, md: 3 },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }
          }}
        >
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
              gap: 1.5,
              py: 1.25,
              px: 2
            }}
          >
            <Typography component="div" variant="subtitle1" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {connectorGuideDialogTitle}
            </Typography>
            <CloseButton ariaLabel="close guide" onClick={handleCloseConnectorGuide} />
          </DialogTitle>
          <DialogContent sx={{ "--dialog-content-top-padding": "0px", p: 0, flex: 1, minHeight: 0, overflow: 'hidden', backgroundColor: '#fff' }}>
            {normalizedGuideUrl ? (
              <iframe
                src={normalizedGuideUrl}
                title={`${connectorGuideDialogTitle} guide`}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 0,
                  display: 'block',
                  backgroundColor: '#fff'
                }}
              />
            ) : (
              <Box sx={{ p: 2.5 }}>
                <Typography color="error" variant="body2">
                  {t('generic_connector.open_guide') || 'Guide URL is missing.'}
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
        <PluginMemoryDialog
          open={isPluginMemoryDialogOpen}
          onClose={() => setIsPluginMemoryDialogOpen(false)}
          memory={effectivePluginMemory}
          pluginName={title || type}
        />
      </Box >
    </LocalizationProvider >
  );
};

export default GenericConnector;
